import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  // Aliased because `Tooltip` is already imported from recharts below.
  Tooltip as MuiTooltip,
} from '@mui/material';
import { ArrowBack as BackIcon, Refresh as RefreshIcon, Edit as EditIcon, ExpandMore as ExpandMoreIcon, PersonOutline as PersonIcon, Download as DownloadIcon, Receipt as ReceiptIcon, Description as DescriptionIcon, Delete as DeleteIcon, Email as EmailIcon, Search as SearchIcon } from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { adminApi, Organization, FeatureDefinition, FeatureCategory, OrganizationInvoice, OrgBillingSummary, Job } from '../services/api';
import JobRunsTable from '../components/JobRunsTable';
import { ADMIN_RUNS_PER_PAGE } from '../constants/pagination';
import FeatureSelector from '../components/FeatureSelector';
import { AdminAssignableTier, ManagedPlanTier, TIER_CATALOGUE, TIER_CODES_ORDERED, MANAGED_TIER_CODES_ORDERED, TierSelectValue } from '../constants/subscription-tiers';
import {
  BRANDING_IMAGE_DIMENSIONS,
  SUPPORTED_DOCUMENT_FONTS,
  DEFAULT_DOCUMENT_FONT,
  isSupportedDocumentFont,
  DOCUMENT_FONT_ROLES,
  DOCUMENT_COLOR_ROLES,
  DEFAULT_DOCUMENT_COLOR,
  toColorInputValue,
  toSafeDocumentColor,
  type DocumentColorRole,
  type DocumentFontRole,
} from '../config/branding';
import BrandingRow from '../components/branding/BrandingRow';
import DocumentStylePreview from '../components/branding/DocumentStylePreview';
import { validateBrandingImageFile } from '../utils/branding-image-validation';
import { EMAIL_BRANDING_IMAGE_DIMENSIONS } from '../config/email-branding';
import { validateEmailBrandingImageFile } from '../utils/email-branding-image-validation';
import { downloadBase64File } from '../utils/download-file';
import { useDebounce } from '../hooks/useDebounce';
import ReferralSourceSelect from '../components/ReferralSourceSelect';
import AccountManagerSelect from '../components/AccountManagerSelect';
import { IS_BROKER_ADMIN } from '../config/admin-build';
import {
  BillingMode,
  deriveBillingMode,
  BILLING_MODE_META,
  billingModesForBuild,
  buildInvoicedAsSummary,
  missingInvoiceFields,
  INVOICE_DESCRIPTION_MAX_CHARS,
  formatMissingFields,
  isGeneratableInvoicePeriod,
  isNextMonthPeriod,
} from './organization-billing-mode';
import { formatZar } from '../utils/format';
import { useUsdZarRate } from '../hooks/useUsdZarRate';
import {
  hasDocAssistData,
  resolveCompletedJobs,
  resolveDocAssistCount,
  DOC_ASSIST_COLOR,
  toUsageChartRows,
  type OrgUsageRow,
} from '../utils/org-usage-stats';

// Open a backend-issued download URL (presigned S3, always https) in a new tab,
// hardened: https-only scheme guard blocks a tampered javascript:/data: URL from
// executing in the admin session, and noopener,noreferrer prevents the opened tab
// from reaching back to window.opener (reverse tabnabbing). Shared by every
// export/invoice download in this view (per abstract-before-duplicating).
function openDownloadInNewTab(url: string | null | undefined): void {
  if (typeof url === 'string' && url.startsWith('https://')) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

// Tier <MenuItem> rows ("<name> — <N> runs/mo") shared by the Managed-plan and
// Generate-payment-link Selects (identical bodies). Extracted per
// abstract-before-duplicating so a future tier-row change is one edit. The
// per-seat member dropdown renders only the name, so it deliberately does NOT
// use this.
const tierRunsMenuItems = (codes: ManagedPlanTier[] = TIER_CODES_ORDERED) =>
  codes.map((code) => (
    <MenuItem key={code} value={code}>
      {TIER_CATALOGUE[code].displayName} — {TIER_CATALOGUE[code].monthlyRunsQuota} runs/mo
    </MenuItem>
  ));

// Billing-status chip presentation for the Subscription & Credits panel
// (Trello #319). Keyed by the backend `billingStatus` enum.
const BILLING_STATUS_META: Record<
  OrgBillingSummary['billingStatus'],
  { label: string; color: 'success' | 'info' | 'secondary' | 'warning' | 'default' }
> = {
  paid: { label: 'Paid', color: 'success' },
  trial: { label: 'Trial', color: 'info' },
  managed: { label: 'Managed (comp\'d)', color: 'secondary' },
  unpaid: { label: 'Unpaid', color: 'warning' },
  none: { label: 'No subscription', color: 'default' },
};

/** Format a number for the credit tiles; em-dash for null/undefined. */
const fmtCredits = (n: number | null | undefined): string =>
  typeof n === 'number' ? n.toLocaleString() : '—';

/** Format an ISO date for the billing tiles; em-dash for null/undefined. */
const fmtBillingDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

/** Format ZAR rands for the price tile; em-dash for null/undefined. */
const fmtRand = (r: number | null | undefined): string =>
  typeof r === 'number' ? `R${r.toLocaleString(undefined, { minimumFractionDigits: 0 })}` : '—';

// ── Billing-method selector (Trello #332 redesign) ──
// The redesigned "How is this org billed?" control is the DEFAULT for the broker-admin
// build: picking a method IS the switch (with a confirm), only the active method's
// fields show, and selecting a method clears the conflicting flags — so the
// derived-from-3-flags conflict (an org both managed AND eftPayer, e.g. Alwyn-Bamberger)
// can no longer happen. The legacy preview-chip model remains only for the IA build.
// (Was behind a prod-off feature flag; trialled on staging, now always on for broker.)

// Display ORDER for the direct-set selector (self-serve → EFT → comp'd). Membership is
// validated against BILLING_MODE_META, which is the single source of truth for a mode's
// broker/selectable flags AND its selectorLabel + confirmEffect — so a mode whose
// `broker` flag flips (or a non-selectable one) drops out here automatically, and there
// are no forked label/effect maps to drift (abstract-before-duplicating).
// Both builds now use this selector (Trello #664). The IA build previously had a
// PREVIEW-CHIP model whose chips could not actually switch anything — its only
// non-managed panel is an informational Alert — so an IA admin could look at a
// mode and never set it. That mattered the moment IA needed to mark orgs as EFT
// payers. One selector rather than two models, per abstract-before-duplicating.
const METHOD_ORDER: BillingMode[] = IS_BROKER_ADMIN
  ? (['selfserve', 'eft', 'managed'] as BillingMode[])
  : (['manual', 'eft'] as BillingMode[]);
const BILLING_METHODS: BillingMode[] = METHOD_ORDER.filter((m) =>
  billingModesForBuild(IS_BROKER_ADMIN).includes(m),
);

interface MemberInfo {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  isPaid: boolean;
  isEnabled: boolean;
  createdAt: string;
  // Active per-user tier seat (broker-AI only). Returned by
  // GET /admin/organizations/:orgId/members when the user has an active
  // UserSubscriptionRecord. Absent when there's no seat or only a canceled
  // seat — frontend renders the dropdown as "None" in that case.
  tier?: AdminAssignableTier;
  monthlyRunQuota?: number;
  priceZAR?: number;
  usage?: {
    submittedCount: number;
    completedCount: number;
  };
}

// One period of org usage is `OrgUsageRow` (utils/org-usage-stats.ts), which also
// owns every read of the Trello #372 split fields (`completedJobsCount` /
// `docAssistCount` — optional, because this admin can deploy ahead of the backend
// that supplies them).

const OrganizationDetailPage: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [members, setMembers] = useState<MemberInfo[]>([]);
  // Members search (Trello #607). Runs SERVER-side because the list is
  // paginated — see the comment on the search box below.
  const [memberSearch, setMemberSearch] = useState<string>('');
  const debouncedMemberSearch = useDebounce(memberSearch);
  // The backend bounds how far a search scans. When it stops short, say so —
  // a short list that looks complete is the bug this search exists to prevent.
  const [membersSearchTruncated, setMembersSearchTruncated] = useState<boolean>(false);
  const [memberCount, setMemberCount] = useState<number>(0); // Total member count from API
  const [usageHistory, setUsageHistory] = useState<OrgUsageRow[]>([]);
  // Billing + package + credit summary (Trello #319). null until loaded / when
  // the backend hasn't populated it (deploy-ordering: backend ships first).
  const [billing, setBilling] = useState<OrgBillingSummary | null>(null);

  // Apply an org-detail refetch response to state — keeps `organization` AND
  // `billing` in sync at EVERY refetch site (managed-plan activate/revert,
  // owner/branding/font/billing edits) so a change that touches the subscription
  // never leaves a stale credit balance on the panel (Trello #319 ai_review).
  // One helper instead of repeating the two setters at each caller.
  const applyOrgResponse = (response: {
    success?: boolean;
    data?: { organization: Organization; billing?: OrgBillingSummary };
  }) => {
    if (response?.success && response.data) {
      setOrganization(response.data.organization);
      setBilling(response.data.billing || null);
    }
  };
  const [allFeatures, setAllFeatures] = useState<FeatureDefinition[]>([]);
  const [featuresByCategory, setFeaturesByCategory] = useState<Record<FeatureCategory, FeatureDefinition[]>>({} as any);
  const [categoryLabels, setCategoryLabels] = useState<Record<FeatureCategory, string>>({} as any);
  const [loading, setLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [membersError, setMembersError] = useState<string | null>(null);
  const [editFeaturesOpen, setEditFeaturesOpen] = useState(false);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [savingFeatures, setSavingFeatures] = useState(false);
  const [featuresError, setFeaturesError] = useState<string | null>(null);
  const [editAccountManagerOpen, setEditAccountManagerOpen] = useState(false);
  const [accountManagerName, setAccountManagerName] = useState('');
  const [accountManagerEmail, setAccountManagerEmail] = useState('');
  const [savingAccountManager, setSavingAccountManager] = useState(false);
  const [accountManagerError, setAccountManagerError] = useState<string | null>(null);
  const [editBillingOpen, setEditBillingOpen] = useState(false);
  const [billingName, setBillingName] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [billingVatNumber, setBillingVatNumber] = useState('');
  const [billingCostPerLicense, setBillingCostPerLicense] = useState('');
  const [billingTierName, setBillingTierName] = useState('');
  const [billingMonthlyAmount, setBillingMonthlyAmount] = useState('');
  /** The org's invoice description line (Trello #670). Empty = brand default. */
  const [billingInvoiceDescription, setBillingInvoiceDescription] = useState('');
  const [savingBilling, setSavingBilling] = useState(false);
  const [billingError, setBillingError] = useState<string | null>(null);
  // Billing-mode redesign: which mode's controls the admin is viewing. null =
  // follow the org's derived current mode; a value = the admin clicked a mode
  // chip to see/change that mode. Reset to null whenever the org reloads.
  const [editOrgDetailsOpen, setEditOrgDetailsOpen] = useState(false);
  const [orgDisplayName, setOrgDisplayName] = useState('');
  const [orgDomain, setOrgDomain] = useState('');
  const [orgMaxRuns, setOrgMaxRuns] = useState('');
  const [orgMaxDocChat, setOrgMaxDocChat] = useState('');
  const [orgMaxResearch, setOrgMaxResearch] = useState('');
  const [orgSharedRunPool, setOrgSharedRunPool] = useState(true);
  const [orgMfaRequired, setOrgMfaRequired] = useState(false);
  const [orgReferralSource, setOrgReferralSource] = useState('');
  // EFT payer (Trello #299): org pays by direct bank transfer — counts toward
  // FSP Hub commission. Broker-AI build only (billing is a broker concern).
  const [orgEftPayer, setOrgEftPayer] = useState(false);
  // Manually-set billing status (IA tenant only) — '' = not set (org shows blank).
  const [orgManualBillingStatus, setOrgManualBillingStatus] = useState('');
  const [savingOrgDetails, setSavingOrgDetails] = useState(false);
  const [orgDetailsError, setOrgDetailsError] = useState<string | null>(null);
  const [brandingUploading, setBrandingUploading] = useState(false);
  const [brandingError, setBrandingError] = useState<string | null>(null);
  const [brandingSuccess, setBrandingSuccess] = useState<string | null>(null);
  const [brandingPreviewFile, setBrandingPreviewFile] = useState<File | null>(null);
  const [brandingPreviewUrl, setBrandingPreviewUrl] = useState<string | null>(null);
  const [emailBrandingUploading, setEmailBrandingUploading] = useState(false);
  const [emailBrandingError, setEmailBrandingError] = useState<string | null>(null);
  const [emailBrandingSuccess, setEmailBrandingSuccess] = useState<string | null>(null);
  const [emailBrandingPreviewFile, setEmailBrandingPreviewFile] = useState<File | null>(null);
  const [emailBrandingPreviewUrl, setEmailBrandingPreviewUrl] = useState<string | null>(null);
  const [fontSaving, setFontSaving] = useState(false);
  // Document colours are edited as a DRAFT and saved explicitly, unlike the font
  // (which auto-saves on Select change). `<input type="color">` fires onChange
  // continuously while the user drags the picker, so auto-saving would put a PUT
  // on every pixel of movement.
  const [colorDraft, setColorDraft] = useState<Record<DocumentColorRole, string> | null>(null);
  const [colorsSaving, setColorsSaving] = useState(false);
  // Which branding row is expanded. One at a time — the card's job at rest is
  // to answer "is this org set up?", and four simultaneously-open editors is
  // the sprawl this layout replaced.
  //
  // Every toggle uses the FUNCTIONAL updater. Reading `brandingEditing` out of
  // the render closure means two updates batched before a re-render both see
  // the pre-click value, so a fast double-click sets the same target twice and
  // the row never closes.
  const [brandingEditing, setBrandingEditing] = useState<
    'logo' | 'email' | 'font' | 'colours' | null
  >(null);

  /**
   * Leaving the colours row abandons its unsaved edits.
   *
   * The preview reads the DRAFT and Discard is only reachable while the row is
   * open, so a draft surviving a close left the preview showing uncommitted
   * colours under a "Live preview" caption with no way to clear them — it read
   * as the org's saved branding and was not.
   *
   * Keyed on `brandingEditing` rather than done inside the toggle, so it fires
   * for every route out of that row (Close, opening another row, anything a
   * later change adds) and keeps the state updater pure.
   */
  useEffect(() => {
    if (brandingEditing !== 'colours') setColorDraft(null);
  }, [brandingEditing]);

  useEffect(() => {
    return () => {
      if (brandingPreviewUrl) URL.revokeObjectURL(brandingPreviewUrl);
    };
  }, [brandingPreviewUrl]);

  useEffect(() => {
    return () => {
      if (emailBrandingPreviewUrl) URL.revokeObjectURL(emailBrandingPreviewUrl);
    };
  }, [emailBrandingPreviewUrl]);
  
  // Invoice state
  const [invoices, setInvoices] = useState<OrganizationInvoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesError, setInvoicesError] = useState<string | null>(null);

  // Invoice email-send state (per-period sending indicator + result toasts)
  const [invoiceSendingPeriod, setInvoiceSendingPeriod] = useState<string | null>(null);
  const [invoiceSendSuccess, setInvoiceSendSuccess] = useState<string | null>(null);
  const [invoiceSendError, setInvoiceSendError] = useState<string | null>(null);

  // Invoice generation modal state
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [invoiceModalPeriod, setInvoiceModalPeriod] = useState<string>('');
  // No customer-name / address / VAT / description / licences / amount state
  // here any more (Trello #670) — the invoice is built from the org record, so
  // there is nothing for this modal to hold. Those six fields were collected,
  // POSTed, and thrown away by the backend.
  const [invoiceGenerating, setInvoiceGenerating] = useState(false);
  const [invoiceModalError, setInvoiceModalError] = useState<string | null>(null);

  // Org credit-pool counter-reset state (run at pool enrollment so pre-existing
  // per-user usage can't instantly cap the shared org pool).
  const [poolResetConfirmOpen, setPoolResetConfirmOpen] = useState(false);
  const [poolResetting, setPoolResetting] = useState(false);
  const [poolResetError, setPoolResetError] = useState<string | null>(null);
  const [poolResetSuccess, setPoolResetSuccess] = useState<string | null>(null);

  // Managed (comp'd) plan activation. 'managed' = paid for by us → no payment
  // page. Set ONLY here (via the Managed plan card → setManagedSubscription),
  // never via the generic org-update PUT.
  const [managedTier, setManagedTier] = useState<ManagedPlanTier>('bronze');
  const [activatingManaged, setActivatingManaged] = useState(false);
  const [managedError, setManagedError] = useState<string | null>(null);
  const [managedSuccess, setManagedSuccess] = useState<string | null>(null);
  const [managedConfirmOpen, setManagedConfirmOpen] = useState(false);

  // Organisation owner + generate-payment-link (Part 2). The owner is one of the
  // org's own members; an admin generates a Stitch payment link that bills them.
  const [selectedOwnerUserId, setSelectedOwnerUserId] = useState<string>('');
  const [savingOwner, setSavingOwner] = useState(false);
  const [ownerError, setOwnerError] = useState<string | null>(null);
  const [ownerSuccess, setOwnerSuccess] = useState<string | null>(null);
  const [paymentLinkTier, setPaymentLinkTier] = useState<'bronze' | 'silver' | 'gold'>('bronze');
  const [generatingLink, setGeneratingLink] = useState(false);
  const [paymentLinkUrl, setPaymentLinkUrl] = useState<string | null>(null);
  const [paymentLinkError, setPaymentLinkError] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // Keep the owner Select in sync with the loaded org's saved owner.
  useEffect(() => {
    setSelectedOwnerUserId(organization?.ownerUserId || '');
  }, [organization?.ownerUserId]);

  // Broker AI orgs get the new auto-pulled PDF invoice (tier + flat monthly
  // amount from the org record); other stacks keep the manual-entry DOCX invoice.
  const isBrokerAi = organization?.stack === 'broker-ai';
  // An org is "enrolled" in the shared credit pool once the ORG_CREDIT_POOL
  // feature is enabled on it (via the Features editor above). The reset action
  // only appears for enrolled orgs.
  const isPoolEnrolled = (organization?.enabledFeatures || organization?.features || []).includes('ORG_CREDIT_POOL');
  // Which billing fields still block invoice generation, BY NAME — so the admin
  // is told "billing address" rather than a generic "set them in Edit Billing
  // Information" (Trello #360). Shared pure helper, mirrors the backend's own
  // InvoiceBillingDataError list so the button and the server agree.
  //
  // NOT gated on `isBrokerAi` since Trello #670. The backend requires the same
  // three fields of an IA org, so gating this meant the IA build rendered no
  // warning and left Generate enabled on an org that could not be invoiced —
  // the admin pressed it seven times and got a bare 400 each time.
  const missingBillingFields = missingInvoiceFields(organization);
  const billingCompleteForInvoice = missingBillingFields.length === 0;
  // What the invoice's description row says when the org has set no
  // `invoiceDescription` — the backend brand profile's `descriptionLabel`
  // (`handlers/admin/invoices/{broker-ai,intelligent-automation}/invoice-brand.ts`),
  // which `buildInvoicePdf` renders as "<label> — <Month Year>". Shown as the
  // placeholder + the read-only fallback so an admin can see what the document
  // will actually say before deciding whether to override it.
  const defaultInvoiceDescriptionLabel = isBrokerAi
    ? 'Broker AI Subscription'
    : 'Intelligent Automation Subscription';

  // The org’s derived current billing mode — the organising principle of the
  // consolidated "Billing & Plan" section below. There used to be a second
  // `currentBillingMode` alias for a PREVIEW mode the legacy chip selector could
  // set; both builds now use the direct-set selector (Trello #664), where picking
  // a method IS the switch, so the preview state could never be non-null and the
  // alias always equalled this value.
  const currentBillingMode = deriveBillingMode(organization, IS_BROKER_ADMIN);
  const monthlyLicenseTotal =
    typeof organization?.costPerLicenseZAR === 'number' && memberCount > 0
      ? organization.costPerLicenseZAR * memberCount
      : null;
  // Live "how the next invoice is built" summary for the active mode (pure helper).
  const invoicedAsSummary = buildInvoicedAsSummary(currentBillingMode, organization, memberCount, billing);

  // Job Export state
  const [jobExports, setJobExports] = useState<Array<{
    period: string;
    exists: boolean;
    url: string | null;
    generatedAt: string | null;
    fileName: string | null;
    jobCount: number | null;
  }>>([]);
  const [jobExportsLoading, setJobExportsLoading] = useState(false);
  const [jobExportsError, setJobExportsError] = useState<string | null>(null);
  const [exportingJobPeriod, setExportingJobPeriod] = useState<string | null>(null);
  // Internal runs+cost CSV export (IA admin build only — exposes cost).
  const [exportingRunsPeriod, setExportingRunsPeriod] = useState<string | null>(null);
  const fxRate = useUsdZarRate();

  // Custom date-range export (e.g. "total runs over the last 90 days"). Defaults to
  // the last 90 days so the most-common ask is one click. YYYY-MM-DD strings compare
  // lexicographically = chronologically, so plain string ordering is safe here.
  const [customRangeFrom, setCustomRangeFrom] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return d.toISOString().slice(0, 10);
  });
  const [customRangeTo, setCustomRangeTo] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [customRangeExporting, setCustomRangeExporting] = useState(false);
  const [customRangeError, setCustomRangeError] = useState<string | null>(null);
  const [customRangeResult, setCustomRangeResult] = useState<{ jobCount: number; from: string; to: string } | null>(null);

  // Delete organization state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Pagination (using DynamoDB LastEvaluatedKey pattern)
  const [hasMoreMembers, setHasMoreMembers] = useState(false);
  const [membersNextKey, setMembersNextKey] = useState<string | undefined>();

  // Runs (jobs) state — live job history for the org, fetched via the
  // OrganizationIndex GSI (cheap, org-scoped). Mirrors the dashboard row shape.
  const [orgJobs, setOrgJobs] = useState<Job[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsError, setJobsError] = useState<string | null>(null);
  const [jobsStatusFilter, setJobsStatusFilter] = useState<'' | 'PROCESSING' | 'QUEUED' | 'COMPLETED' | 'FAILED'>('');
  // Offset-based pagination, mirroring the dashboard Recent Runs table:
  // a precise "1–N of TOTAL" navigator (shared page size with the dashboard).
  const [jobsPage, setJobsPage] = useState(0);
  const [jobsTotal, setJobsTotal] = useState(0);

  // Fetch organization details
  useEffect(() => {
    const fetchOrgDetails = async () => {
      if (!orgId) return;

      try {
        setLoading(true);
        const [orgResponse, featuresResponse] = await Promise.all([
          adminApi.getOrganization(orgId),
          adminApi.listFeatures()
        ]);
        
        if (orgResponse.success) {
          setOrganization(orgResponse.data.organization);
          setUsageHistory(orgResponse.data.usageHistory || []);
          setBilling(orgResponse.data.billing || null);
          setMemberCount(orgResponse.data.memberCount || 0); // Set total member count
          // Restore the managed-plan dropdown to the org's saved tier so a
          // browser refresh doesn't reset it to Bronze (Trello #280). Self-serve
          // orgs have no managedTier. Validate against the known managed set so
          // an unexpected backend value can't reach the dropdown / confirm dialog.
          const savedManagedTier = orgResponse.data.organization.managedTier;
          if (savedManagedTier && MANAGED_TIER_CODES_ORDERED.includes(savedManagedTier)) {
            setManagedTier(savedManagedTier);
          }
        }

        if (featuresResponse.success) {
          setAllFeatures(featuresResponse.data.features);
          setFeaturesByCategory(featuresResponse.data.byCategory);
          setCategoryLabels(featuresResponse.data.categoryLabels);
        }
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to load organization');
      } finally {
        setLoading(false);
      }
    };

    fetchOrgDetails();
  }, [orgId]);

  // Current month usage is the first item in the history
  const currentUsage = usageHistory.length > 0 ? usageHistory[0] : null;

  // Trello #372 — Doc Assist questions are chat turns, not platform jobs, but each
  // registers a job row so they inflate the completed total. Show them as their own
  // figure and keep them out of "Completed Jobs". Gated on the backend actually
  // reporting the split, so an admin deployed ahead of the API renders the old
  // (accurate-for-what-it-knows) view instead of a misleading permanent 0.
  const showDocAssistStat = hasDocAssistData(usageHistory);
  const usageChartRows = toUsageChartRows(usageHistory);

  // Fetch members from DynamoDB user-profiles table via GSI
  const fetchMembers = useCallback(async (lastKey?: string) => {
    if (!orgId) return;

    try {
      setMembersLoading(true);
      setMembersError(null);
      
      const response = await adminApi.listOrganizationMembers(orgId, {
        limit: 50,
        lastKey,
        includeUsage: true,
        ...(debouncedMemberSearch && { search: debouncedMemberSearch })
      });
      
      if (response.success) {
        if (lastKey) {
          // Append to existing members (pagination)
          setMembers(prev => [...prev, ...response.data.members]);
        } else {
          // Replace members (initial load or refresh)
          setMembers(response.data.members);
        }
        setHasMoreMembers(response.data.pagination.hasMore);
        setMembersNextKey(response.data.pagination.nextKey);
        setMembersSearchTruncated(response.data.searchTruncated === true);
      }
    } catch (err: any) {
      setMembersError(err.response?.data?.message || err.message || 'Failed to load members');
    } finally {
      setMembersLoading(false);
    }
  }, [orgId, debouncedMemberSearch]);

  // Fetch members on mount, and again whenever the debounced search changes
  // (fetchMembers is re-created when the term changes, so this covers both).
  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  // Fetch the org's runs (jobs) via the OrganizationIndex GSI. Newest-first,
  // paginated by opaque startKey. Depends on the status filter so changing the
  // filter re-fetches from page 1.
  const fetchJobs = useCallback(async () => {
    if (!orgId) return;

    try {
      setJobsLoading(true);
      setJobsError(null);

      const response = await adminApi.listOrganizationJobs(orgId, {
        offset: jobsPage * ADMIN_RUNS_PER_PAGE,
        limit: ADMIN_RUNS_PER_PAGE,
        status: jobsStatusFilter || undefined,
      });

      if (response.success) {
        setOrgJobs(response.data.jobs);
        setJobsTotal(response.data.pagination.total ?? response.data.jobs.length);
      }
    } catch (err: any) {
      setJobsError(err.response?.data?.message || err.message || 'Failed to load runs');
    } finally {
      setJobsLoading(false);
    }
  }, [orgId, jobsStatusFilter, jobsPage]);

  // Fetch runs on mount and whenever the page or status filter changes
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Fetch invoices
  const fetchInvoices = useCallback(async () => {
    if (!orgId) return;

    try {
      setInvoicesLoading(true);
      setInvoicesError(null);
      
      const response = await adminApi.listOrganizationInvoices(orgId);
      
      if (response.success) {
        setInvoices(response.data.invoices);
      }
    } catch (err: any) {
      setInvoicesError(err.response?.data?.message || err.message || 'Failed to load invoices');
    } finally {
      setInvoicesLoading(false);
    }
  }, [orgId]);

  // Fetch invoices on mount
  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Fetch job exports (check S3 for existing exports)
  const fetchJobExports = useCallback(async () => {
    if (!orgId) return;

    try {
      setJobExportsLoading(true);
      setJobExportsError(null);
      
      const response = await adminApi.listOrganizationJobExports(orgId);
      
      if (response.success) {
        setJobExports(response.data.exports);
      }
    } catch (err: any) {
      setJobExportsError(err.response?.data?.message || err.message || 'Failed to load job exports');
    } finally {
      setJobExportsLoading(false);
    }
  }, [orgId]);

  // Fetch job exports when mounted or when orgId changes
  useEffect(() => {
    fetchJobExports();
  }, [fetchJobExports]);

  // Email an already-generated invoice to the org's account manager.
  const handleSendInvoice = async (period: string) => {
    if (!orgId) return;
    setInvoiceSendError(null);
    setInvoiceSendSuccess(null);
    setInvoiceSendingPeriod(period);
    try {
      const response = await adminApi.sendOrganizationInvoice(orgId, period);
      const sentTo = response?.data?.sentTo;
      setInvoiceSendSuccess(
        `Invoice for ${formatPeriod(period)} sent${sentTo ? ` to ${sentTo}` : ''}.`,
      );
    } catch (err: any) {
      setInvoiceSendError(
        err.response?.data?.message || err.message || 'Failed to send invoice',
      );
    } finally {
      setInvoiceSendingPeriod(null);
    }
  };

  // Opens the confirmation modal. Everything the invoice prints is read from the
  // org record by the backend, so there is nothing to seed here beyond the period
  // (Trello #670). This used to derive a customer name, address, VAT, licence
  // count, amount and description into modal state — all six were POSTed and all
  // six were discarded by the backend, which reads the org record instead.
  const handleGenerateInvoice = async (period: string) => {
    if (!orgId || !organization) return;
    setInvoiceModalPeriod(period);
    setInvoiceModalError(null);
    setInvoiceModalOpen(true);
  };

  const handleExportJobs = async (period: string) => {
    if (!orgId) return;
    
    try {
      setExportingJobPeriod(period);
      setJobExportsError(null);
      
      const response = await adminApi.exportOrganizationJobs(orgId, period);
      
      if (response.success && response.data?.downloadUrl) {
        // Open download in new tab
        openDownloadInNewTab(response.data.downloadUrl);
        
        // Refresh the exports list to show the newly generated export
        await fetchJobExports();
      } else {
        throw new Error('No download URL returned');
      }
    } catch (err: any) {
      setJobExportsError(err.response?.data?.message || err.message || 'Failed to export jobs');
    } finally {
      setExportingJobPeriod(null);
    }
  };

  // INTERNAL: download the per-run runs+cost CSV (IA admin build only). Decodes the
  // base64 the backend returns into a Blob so the browser saves it directly — the
  // byte-for-byte decode preserves UTF-8 (workflow names) in the CSV.
  const handleExportRunsCsv = async (period: string) => {
    if (!orgId) return;
    try {
      setExportingRunsPeriod(period);
      setJobExportsError(null);

      const response = await adminApi.exportOrganizationRunsCsv(
        orgId,
        period,
        fxRate.rate ?? undefined,
      );

      if (response.success && response.data?.csvBase64) {
        downloadBase64File(
          response.data.csvBase64,
          response.data.filename || `runs-cost_${period}.csv`,
          'text/csv;charset=utf-8;',
        );
      } else {
        throw new Error('No CSV returned');
      }
    } catch (err: any) {
      setJobExportsError(
        err.response?.data?.message || err.message || 'Failed to export runs + cost',
      );
    } finally {
      setExportingRunsPeriod(null);
    }
  };

  const handleExportCustomRange = async () => {
    if (!orgId || !customRangeFrom || !customRangeTo) return;
    if (customRangeFrom > customRangeTo) {
      setCustomRangeError('The "From" date must be on or before the "To" date.');
      return;
    }

    try {
      setCustomRangeExporting(true);
      setCustomRangeError(null);
      setCustomRangeResult(null);

      const response = await adminApi.exportOrganizationJobs(orgId, {
        startDate: customRangeFrom,
        endDate: customRangeTo,
      });

      if (response.success && response.data?.downloadUrl) {
        openDownloadInNewTab(response.data.downloadUrl);
        setCustomRangeResult({
          jobCount: response.data.jobCount ?? 0,
          from: customRangeFrom,
          to: customRangeTo,
        });
      } else {
        throw new Error('No download URL returned');
      }
    } catch (err: any) {
      setCustomRangeError(err.response?.data?.message || err.message || 'Failed to export custom range');
    } finally {
      setCustomRangeExporting(false);
    }
  };

  const handleResetPoolCounter = async () => {
    if (!orgId) return;
    try {
      setPoolResetting(true);
      setPoolResetError(null);
      setPoolResetSuccess(null);
      const res = await adminApi.resetOrgPoolCounter(orgId);
      const prev = res?.data?.previousCount;
      const period = res?.data?.period;
      setPoolResetSuccess(
        `Pool counter reset for ${period ?? 'the current period'}` +
          (typeof prev === 'number'
            ? ` — was ${prev} run${prev === 1 ? '' : 's'}, now 0. Reload to see the updated count.`
            : '. Reload to see the updated count.'),
      );
      setPoolResetConfirmOpen(false);
    } catch (err: any) {
      setPoolResetError(err.response?.data?.message || err.message || 'Failed to reset pool counter');
    } finally {
      setPoolResetting(false);
    }
  };

  // ONE path for both stacks (Trello #670). Every invoice — broker-AI and
  // Intelligent Automation alike — is built from the organisation record: name,
  // address, VAT, tier, amount, and now the description line. There is no
  // request body, because there has been nowhere for one to go since the
  // branded-PDF rewrite; the IA branch that sent six fields was sending them
  // into a `bodyIgnored: true` log line.
  const handleConfirmGenerateInvoice = async () => {
    if (!orgId) return;
    try {
      setInvoiceGenerating(true);
      setInvoiceModalError(null);
      const response = await adminApi.generateOrganizationInvoice(orgId, invoiceModalPeriod);
      if (response.success && response.data.url) {
        openDownloadInNewTab(response.data.url);
        await fetchInvoices();
        setInvoiceModalOpen(false);
      }
    } catch (err: any) {
      setInvoiceModalError(err.response?.data?.message || err.message || 'Failed to generate invoice');
    } finally {
      setInvoiceGenerating(false);
    }
  };

  const formatPeriod = (period: string): string => {
    const [year, month] = period.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const handleRefreshMembers = () => {
    setMembersNextKey(undefined);
    fetchMembers();
  };

  // Inline per-row tier assign. Broker-AI orgs only. Selecting "" clears the
  // seat (backend cancels by writing tier: null). Optimistic update on the
  // row, then refetch to pick up canonical fields (priceZAR / quota) from
  // the catalogue.
  const [savingTierForUserId, setSavingTierForUserId] = useState<string | null>(null);
  const [memberTierError, setMemberTierError] = useState<string | null>(null);
  const handleMemberTierChange = async (userId: string, next: TierSelectValue) => {
    try {
      setSavingTierForUserId(userId);
      setMemberTierError(null);
      const tierUpdate: AdminAssignableTier | null = next === ''
        ? null
        : (next as AdminAssignableTier);
      await adminApi.updateUser(userId, { tier: tierUpdate });
      // Refresh the row from canonical state (priceZAR + quota are snapped
      // server-side from the catalogue at write time).
      setMembersNextKey(undefined);
      await fetchMembers();
    } catch (err: any) {
      setMemberTierError(
        err?.response?.data?.message
          || err?.message
          || 'Failed to update tier',
      );
    } finally {
      setSavingTierForUserId(null);
    }
  };

  const handleLoadMoreMembers = () => {
    if (membersNextKey) {
      fetchMembers(membersNextKey);
    }
  };

  const handleRefreshJobs = () => {
    // Resetting to page 0 re-fetches via the effect; if already on page 0,
    // call directly (no page change to trigger the effect).
    if (jobsPage !== 0) {
      setJobsPage(0);
    } else {
      fetchJobs();
    }
  };

  const handleEditFeaturesClick = () => {
    setSelectedFeatures(organization?.enabledFeatures || organization?.features || []);
    setFeaturesError(null);
    setEditFeaturesOpen(true);
  };

  const handleEditAccountManagerClick = () => {
    setAccountManagerName(organization?.accountManagerName || '');
    setAccountManagerEmail(organization?.accountManagerEmail || '');
    setAccountManagerError(null);
    setEditAccountManagerOpen(true);
  };

  const handleEditBillingClick = () => {
    setBillingName(organization?.billingName || '');
    setBillingAddress(organization?.billingAddress || '');
    setBillingVatNumber(organization?.vatNumber || '');
    setBillingCostPerLicense(String(organization?.costPerLicenseZAR || ''));
    setBillingTierName(organization?.tierName || '');
    setBillingMonthlyAmount(String(organization?.monthlyAmountZAR || ''));
    setBillingInvoiceDescription(organization?.invoiceDescription || '');
    setBillingError(null);
    setEditBillingOpen(true);
  };

  const handleEditOrgDetailsClick = () => {
    setOrgDisplayName(organization?.displayName || '');
    setOrgDomain(organization?.domain || '');
    setOrgMaxRuns(String(organization?.maxRunsPerMonth || 50));
    setOrgMaxDocChat(String(organization?.maxDocChatPerMonth || 50));
    setOrgMaxResearch(String(organization?.maxResearchPerMonth || 50));
    setOrgSharedRunPool(organization?.sharedRunPool !== false);
    setOrgMfaRequired(!!organization?.mfaRequired);
    setOrgReferralSource(organization?.referralSource || '');
    setOrgEftPayer(organization?.eftPayer === true);
    setOrgManualBillingStatus(organization?.manualBillingStatus || '');
    setOrgDetailsError(null);
    setEditOrgDetailsOpen(true);
  };

  const handleSaveAccountManager = async () => {
    if (!orgId) return;

    // Validate email format if provided
    if (accountManagerEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(accountManagerEmail.trim())) {
      setAccountManagerError('Invalid email format');
      return;
    }

    try {
      setSavingAccountManager(true);
      setAccountManagerError(null);
      
      // Send '' — never undefined — so picking "Unassigned" actually CLEARS the
      // fields. update-organization skips any key that is `undefined` and maps
      // an empty string to null, so undefined here would silently keep the old
      // manager. (It also calls .trim() unguarded, so an explicit null throws.)
      await adminApi.updateOrganization(orgId, {
        accountManagerName: accountManagerName.trim(),
        accountManagerEmail: accountManagerEmail.trim()
      });

      // Refresh organization data
      const response = await adminApi.getOrganization(orgId);
      applyOrgResponse(response);

      setEditAccountManagerOpen(false);
    } catch (err: any) {
      setAccountManagerError(err.response?.data?.message || err.message || 'Failed to update account manager');
    } finally {
      setSavingAccountManager(false);
    }
  };

  const handleSaveBilling = async () => {
    if (!orgId) return;

    // Validate cost per license if provided
    const costPerLicense = billingCostPerLicense.trim() ? parseFloat(billingCostPerLicense) : undefined;
    if (billingCostPerLicense.trim() && (isNaN(costPerLicense!) || costPerLicense! < 0)) {
      setBillingError('Cost per license must be a valid positive number');
      return;
    }

    // Validate Broker AI monthly subscription amount if provided
    const monthlyAmount = billingMonthlyAmount.trim() ? parseFloat(billingMonthlyAmount) : undefined;
    if (billingMonthlyAmount.trim() && (isNaN(monthlyAmount!) || monthlyAmount! < 0)) {
      setBillingError('Monthly subscription amount must be a valid positive number');
      return;
    }

    try {
      setSavingBilling(true);
      setBillingError(null);

      await adminApi.updateOrganization(orgId, {
        billingName: billingName.trim() || undefined,
        billingAddress: billingAddress.trim() || undefined,
        vatNumber: billingVatNumber.trim() || undefined,
        costPerLicenseZAR: costPerLicense,
        tierName: billingTierName.trim() || undefined,
        monthlyAmountZAR: monthlyAmount,
        // Always sent, unlike its siblings: an empty string is how the admin
        // CLEARS an override and goes back to the brand default, and `undefined`
        // is the backend's "leave unchanged" signal — so `|| undefined` here
        // would make the field un-clearable once set (Trello #670).
        invoiceDescription: billingInvoiceDescription.trim(),
      });

      // Refresh organization data
      const response = await adminApi.getOrganization(orgId);
      applyOrgResponse(response);

      setEditBillingOpen(false);
    } catch (err: any) {
      setBillingError(err.response?.data?.message || err.message || 'Failed to update billing information');
    } finally {
      setSavingBilling(false);
    }
  };

  const handleSaveOrgDetails = async () => {
    if (!orgId) return;

    // Validate
    const maxRuns = parseInt(orgMaxRuns);
    const maxDocChat = parseInt(orgMaxDocChat);
    const maxResearch = parseInt(orgMaxResearch);
    
    if (isNaN(maxRuns) || maxRuns < 0) {
      setOrgDetailsError('Monthly run limit must be a positive number');
      return;
    }
    // Doc Chat / Legal Research limits are only editable on the IA build
    // (Trello #380) — don't validate fields the broker admin can't see.
    if (!IS_BROKER_ADMIN && (isNaN(maxDocChat) || maxDocChat < 0)) {
      setOrgDetailsError('Document chat limit must be a positive number');
      return;
    }
    if (!IS_BROKER_ADMIN && (isNaN(maxResearch) || maxResearch < 0)) {
      setOrgDetailsError('Legal research limit must be a positive number');
      return;
    }

    try {
      setSavingOrgDetails(true);
      setOrgDetailsError(null);
      
      await adminApi.updateOrganization(orgId, {
        displayName: orgDisplayName.trim() || undefined,
        domain: orgDomain.trim() || undefined,
        maxRunsPerMonth: maxRuns,
        // Omit-on-broker (same rule as referralSource below): the broker build
        // no longer renders these fields, so it must not write them — leaving
        // the stored values untouched rather than persisting stale state.
        maxDocChatPerMonth: IS_BROKER_ADMIN ? undefined : maxDocChat,
        maxResearchPerMonth: IS_BROKER_ADMIN ? undefined : maxResearch,
        sharedRunPool: orgSharedRunPool,
        mfaRequired: orgMfaRequired,
        // The IA admin build never sees/sets the field — omit it there so an
        // org-details save can't wipe an existing broker referral source.
        referralSource: IS_BROKER_ADMIN ? orgReferralSource.trim() : undefined,
        // Sent on BOTH builds since Trello #664 — Intelligent Automation runs the
        // same manual-EFT channel, so an IA admin has to be able to mark an org as
        // an EFT payer. It was omit-on-IA when EFT was broker-only (#299); leaving
        // it omitted would mean the monthly cron never finds an IA org to invoice.
        eftPayer: orgEftPayer,
        // Manual billing status — IA admin only. Send the value (or null to clear
        // it) on the IA build; omit on broker-AI (its billing is auto-derived).
        manualBillingStatus: !IS_BROKER_ADMIN
          ? ((orgManualBillingStatus || null) as 'trial' | 'unpaid' | 'paid' | null)
          : undefined,
      });

      // Refresh organization data
      const response = await adminApi.getOrganization(orgId);
      applyOrgResponse(response);

      setEditOrgDetailsOpen(false);
    } catch (err: any) {
      setOrgDetailsError(err.response?.data?.message || err.message || 'Failed to update organization details');
    } finally {
      setSavingOrgDetails(false);
    }
  };

  // Activate a managed (comp'd) plan: writes an ACTIVE no-Stitch subscription at
  // the chosen tier, flips the org to billingMode='managed', and ensures the
  // ORG_CREDIT_POOL feature — fully comp'ing the org (no payment page; runs work).
  const handleActivateManaged = async () => {
    if (!orgId) return;
    try {
      setActivatingManaged(true);
      setManagedError(null);
      setManagedSuccess(null);
      await adminApi.setManagedSubscription(orgId, managedTier);
      const response = await adminApi.getOrganization(orgId);
      applyOrgResponse(response);
      setManagedSuccess(
        `Managed plan activated on the ${TIER_CATALOGUE[managedTier].displayName} tier (${TIER_CATALOGUE[managedTier].monthlyRunsQuota} runs/mo, no charge).`,
      );
      setManagedConfirmOpen(false);
    } catch (err: any) {
      setManagedError(err.response?.data?.message || err.message || 'Failed to activate managed plan');
    } finally {
      setActivatingManaged(false);
    }
  };


  // Save the chosen owner (one of the org's members). Denormalises the owner's
  // name + email onto the org so generate-payment-link feeds Stitch directly.
  const handleSaveOwner = async () => {
    if (!orgId || !selectedOwnerUserId) return;
    const member = members.find((m) => m.userId === selectedOwnerUserId);
    if (!member) {
      setOwnerError('Selected user is not in this organisation');
      return;
    }
    const ownerName = `${member.firstName || ''} ${member.lastName || ''}`.trim() || undefined;
    try {
      setSavingOwner(true);
      setOwnerError(null);
      setOwnerSuccess(null);
      await adminApi.updateOrganization(orgId, {
        ownerUserId: member.userId,
        ownerName: ownerName ?? null,
        ownerEmail: member.email,
      });
      const response = await adminApi.getOrganization(orgId);
      applyOrgResponse(response);
      setOwnerSuccess(`Owner set to ${member.email}.`);
    } catch (err: any) {
      setOwnerError(err.response?.data?.message || err.message || 'Failed to set owner');
    } finally {
      setSavingOwner(false);
    }
  };

  // ── Billing-method selector ──
  // The redesigned direct-set control. Picking a method opens a confirm; applying it
  // sets the chosen method AND clears the conflicting flags via the existing endpoints,
  // so an org can never end up both managed and eftPayer (the Alwyn-Bamberger bug).
  const [methodConfirm, setMethodConfirm] = useState<BillingMode | null>(null);
  const [methodConfirmTier, setMethodConfirmTier] = useState<ManagedPlanTier>('bronze');
  const [switchingMethod, setSwitchingMethod] = useState(false);
  // Dialog-scoped error so a failed apply's message never lingers in the selector box
  // after the user cancels (it's shown only inside the confirm dialog).
  const [methodError, setMethodError] = useState<string | null>(null);

  const openMethodConfirm = (m: BillingMode) => {
    setMethodError(null);
    setManagedError(null);
    if (m === 'managed') setMethodConfirmTier(managedTier);
    setMethodConfirm(m);
  };

  const applyBillingMethod = async () => {
    if (!orgId || !methodConfirm) return;
    const target = methodConfirm;
    try {
      setSwitchingMethod(true);
      setMethodError(null);
      // Clear conflicting flags + set the target — one atomic-intent operation built
      // from the existing endpoints (a future single setBillingMethod endpoint would
      // make this a single call; see the MR notes).
      if (target === 'managed') {
        if (organization?.eftPayer === true) await adminApi.updateOrganization(orgId, { eftPayer: false });
        await adminApi.setManagedSubscription(orgId, methodConfirmTier);
      } else if (target === 'eft') {
        if (organization?.billingMode === 'managed') await adminApi.revertManagedSubscription(orgId);
        await adminApi.updateOrganization(orgId, { eftPayer: true });
      } else {
        // self-serve = the absence of the other two flags.
        if (organization?.billingMode === 'managed') await adminApi.revertManagedSubscription(orgId);
        if (organization?.eftPayer === true) await adminApi.updateOrganization(orgId, { eftPayer: false });
      }
      const response = await adminApi.getOrganization(orgId);
      applyOrgResponse(response);
      setMethodConfirm(null);
    } catch (err: any) {
      setMethodError(err.response?.data?.message || err.message || 'Failed to switch billing method');
    } finally {
      setSwitchingMethod(false);
    }
  };

  // Generate a Stitch payment link for the org owner at the chosen tier. The
  // returned hosted URL is handed to the owner (they pay without logging in).
  const handleGeneratePaymentLink = async () => {
    if (!orgId) return;
    try {
      setGeneratingLink(true);
      setPaymentLinkError(null);
      setPaymentLinkUrl(null);
      setLinkCopied(false);
      const res = await adminApi.generatePaymentLink(orgId, paymentLinkTier);
      setPaymentLinkUrl(res.data.url);
    } catch (err: any) {
      setPaymentLinkError(
        err.response?.data?.message || err.message || 'Failed to generate payment link',
      );
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopyPaymentLink = async () => {
    if (!paymentLinkUrl) return;
    try {
      await navigator.clipboard.writeText(paymentLinkUrl);
      setLinkCopied(true);
    } catch {
      // Clipboard API can be unavailable (insecure context) — leave the URL
      // visible so the admin can select+copy it manually.
      setLinkCopied(false);
    }
  };

  const handleBrandingFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !orgId) return;

    setBrandingError(null);
    setBrandingSuccess(null);

    const validation = await validateBrandingImageFile(file);
    if (!validation.ok) {
      setBrandingError(validation.message);
      return;
    }

    if (brandingPreviewUrl) URL.revokeObjectURL(brandingPreviewUrl);
    setBrandingPreviewFile(file);
    setBrandingPreviewUrl(URL.createObjectURL(file));
  };

  const handleCancelBrandingPreview = () => {
    if (brandingPreviewUrl) URL.revokeObjectURL(brandingPreviewUrl);
    setBrandingPreviewFile(null);
    setBrandingPreviewUrl(null);
    setBrandingError(null);
  };

  const handleConfirmBrandingUpload = async () => {
    if (!brandingPreviewFile || !orgId) return;

    try {
      setBrandingUploading(true);
      setBrandingError(null);
      await adminApi.uploadOrganizationBranding(orgId, brandingPreviewFile);

      const response = await adminApi.getOrganization(orgId);
      applyOrgResponse(response);
      setBrandingSuccess('Branding logo uploaded successfully');

      if (brandingPreviewUrl) URL.revokeObjectURL(brandingPreviewUrl);
      setBrandingPreviewFile(null);
      setBrandingPreviewUrl(null);
    } catch (err: any) {
      setBrandingError(err.response?.data?.message || err.message || 'Failed to upload branding logo');
    } finally {
      setBrandingUploading(false);
    }
  };

  const handleEmailBrandingFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !orgId) return;

    setEmailBrandingError(null);
    setEmailBrandingSuccess(null);

    const validation = await validateEmailBrandingImageFile(file);
    if (!validation.ok) {
      setEmailBrandingError(validation.message);
      return;
    }

    if (emailBrandingPreviewUrl) URL.revokeObjectURL(emailBrandingPreviewUrl);
    setEmailBrandingPreviewFile(file);
    setEmailBrandingPreviewUrl(URL.createObjectURL(file));
  };

  const handleCancelEmailBrandingPreview = () => {
    if (emailBrandingPreviewUrl) URL.revokeObjectURL(emailBrandingPreviewUrl);
    setEmailBrandingPreviewFile(null);
    setEmailBrandingPreviewUrl(null);
    setEmailBrandingError(null);
  };

  const handleConfirmEmailBrandingUpload = async () => {
    if (!emailBrandingPreviewFile || !orgId) return;

    try {
      setEmailBrandingUploading(true);
      setEmailBrandingError(null);
      await adminApi.uploadOrganizationEmailBranding(orgId, emailBrandingPreviewFile);

      const response = await adminApi.getOrganization(orgId);
      applyOrgResponse(response);
      setEmailBrandingSuccess('Email logo uploaded successfully');

      if (emailBrandingPreviewUrl) URL.revokeObjectURL(emailBrandingPreviewUrl);
      setEmailBrandingPreviewFile(null);
      setEmailBrandingPreviewUrl(null);
    } catch (err: any) {
      setEmailBrandingError(err.response?.data?.message || err.message || 'Failed to upload email logo');
    } finally {
      setEmailBrandingUploading(false);
    }
  };

  /**
   * Save ONE font role (Trello #645).
   *
   * Sends only the role that changed: the backend treats an omitted role as
   * "keep existing", which is exactly right for a picker that saves on change.
   * (The colour pickers send every role because they batch an explicit Save
   * over a draft that may hold several edits at once.)
   */
  const handleFontChange = async (role: DocumentFontRole, nextFont: string) => {
    if (!orgId) return;
    setBrandingError(null);
    setBrandingSuccess(null);
    const label = DOCUMENT_FONT_ROLES.find((r) => r.key === role)?.label ?? role;
    try {
      setFontSaving(true);
      await adminApi.updateOrganization(orgId, { brandingFonts: { [role]: nextFont } });
      const response = await adminApi.getOrganization(orgId);
      applyOrgResponse(response);
      // The two roles revert to DIFFERENT things, so one shared message was
      // wrong for one of them: heading falls back to the body face, not to the
      // platform default. Saying "Calibri" to an admin on an Arial-body org, two
      // hundred lines above a picker that says "Same as body (Arial)", is the
      // picker contradicting itself. (ai_review !207, defects.)
      // Gated on `bodyFaceIsUsable`, not merely on the body face being SET —
      // the same gate the empty MenuItem uses. An org holding the retired
      // "Leitura Sans Grot" would otherwise be told its headings reverted to a
      // face the backend rejects, in the same interaction where the picker
      // correctly said "Default (Calibri)". (ai_review !207.)
      const revertedTo =
        role === 'heading' && bodyFaceIsUsable
          ? `the body face (${storedFonts.body})`
          : `the default (${DEFAULT_DOCUMENT_FONT})`;
      setBrandingSuccess(
        nextFont ? `${label} font set to ${nextFont}` : `${label} font reverted to ${revertedTo}`
      );
    } catch (err: any) {
      setBrandingError(err.response?.data?.message || err.message || 'Failed to update font');
    } finally {
      setFontSaving(false);
    }
  };

  /**
   * What each role is STORED as — empty string when the org has not chosen it.
   *
   * `body` reads the legacy `branding.font` as a fallback because that is
   * precisely what the backend does (resolveOrgFonts), and a picker that showed
   * "Default" for an org whose documents render in Arial would be lying about
   * the state of the system.
   */
  const storedFonts: Record<DocumentFontRole, string> = {
    heading: organization?.branding?.fonts?.heading ?? '',
    body: organization?.branding?.fonts?.body ?? organization?.branding?.font ?? '',
  };

  /**
   * What each role RESOLVES to — never empty. The heading face follows the body
   * face while unset, mirroring the backend chain, so the preview and the row
   * summary show the document the org would actually receive.
   */
  const resolvedFonts: Record<DocumentFontRole, string> = (() => {
    // Filtered through the allowlist, because the backend filters too: a stored
    // face that is no longer supported resolves to the default there, so a row
    // summary naming it would state a face the org's documents never use — the
    // one thing this row exists not to do. The pickers below already gate on it;
    // this makes the summary agree with the pickers it summarises.
    const usable = (face: string | undefined): string | undefined =>
      isSupportedDocumentFont(face) ? face : undefined;
    const body = usable(storedFonts.body) ?? DEFAULT_DOCUMENT_FONT;
    return {
      body,
      heading: usable(storedFonts.heading) ?? body,
    };
  })();

  const brandingFontsAreSet = DOCUMENT_FONT_ROLES.some((role) => !!storedFonts[role.key]);

  /** Is the stored body face one the backend will actually render? */
  const bodyFaceIsUsable = isSupportedDocumentFont(storedFonts.body);

  /**
   * What a role is CURRENTLY painting, which is not always what is stored.
   *
   * `body` is the one role with a non-trivial answer: it did not exist before
   * Trello #645, so an org that set a heading colour is painting its body copy
   * in that heading colour right now — the backend resolves
   * `documentColors.body ?? documentColors.heading`. Seeding the picker from
   * the raw stored field instead would show BLACK, and the next Save would send
   * that black and genuinely repaint their documents. A picker that misreports
   * the current state is worse than no picker; mirror the backend chain.
   */
  const storedColorForRole = (role: DocumentColorRole): string | undefined => {
    const scheme = organization?.branding?.documentColors;
    return role === 'body' ? scheme?.body ?? scheme?.heading : scheme?.[role];
  };

  /**
   * The draft the pickers edit. Seeded from what each role is currently
   * painting on first open and after every save, so "unset" shows the live
   * colour rather than an empty picker.
   */
  const documentColors: Record<DocumentColorRole, string> =
    colorDraft ??
    // Derived from DOCUMENT_COLOR_ROLES rather than written out, for the same
    // reason the reset payload below is: a literal silently stops covering a
    // role added to that list later, with no type error to catch it. Adding
    // `body` in Trello #645 is exactly that case.
    (Object.fromEntries(
      DOCUMENT_COLOR_ROLES.map((role) => [
        role.key,
        toColorInputValue(storedColorForRole(role.key)),
      ]),
    ) as Record<DocumentColorRole, string>);

  /**
   * Whether the org actually OVERRIDES the document colours, as opposed to
   * showing the defaults `documentColors` falls back to. Read from the stored
   * record, never from the draft — an unsaved edit must not make the row claim
   * a scheme is set.
   */
  const brandingColoursAreSet = DOCUMENT_COLOR_ROLES.some(
    (role) => !!organization?.branding?.documentColors?.[role.key],
  );

  /**
   * Toggling a row. The updater is PURE — React documents functional updaters
   * as side-effect-free and StrictMode double-invokes them to prove it, so the
   * draft-clearing this used to do inline lived in exactly the wrong place.
   */
  const setBrandingRow = (next: 'logo' | 'email' | 'font' | 'colours') =>
    setBrandingEditing((prev) => (prev === next ? null : next));

  /** Drives the "N of 4 set" chip — the card's answer to "is this org done?". */
  const brandingSetCount = [
    !!organization?.branding?.logoUrl,
    !!organization?.branding?.emailLogoDownloadUrl,
    brandingFontsAreSet,
    brandingColoursAreSet,
  ].filter(Boolean).length;

  /**
   * Save + reset differ only in their payload and their success line, so they
   * share one lifecycle. Forking them meant a later change to that lifecycle —
   * an optimistic update, different error handling — had to be made twice.
   *
   * A 5xx body can carry internal detail an admin has no use for, so only a
   * 4xx message (which is the validation text the admin needs to act on) is
   * surfaced verbatim.
   */
  const submitColorChange = async (
    brandingColors: Record<DocumentColorRole, string>,
    successMessage: string,
    failureMessage: string,
  ) => {
    if (!orgId) return;
    setBrandingError(null);
    setBrandingSuccess(null);
    try {
      setColorsSaving(true);
      await adminApi.updateOrganization(orgId, { brandingColors });
      const response = await adminApi.getOrganization(orgId);
      applyOrgResponse(response);
      // Re-seed from the server rather than keeping the local draft — the
      // backend normalises (strips `#`), so the draft and the stored value are
      // not the same string.
      setColorDraft(null);
      setBrandingSuccess(successMessage);
    } catch (err: any) {
      const status = err.response?.status;
      const serverMessage = status && status < 500 ? err.response?.data?.message : undefined;
      setBrandingError(serverMessage || failureMessage);
    } finally {
      setColorsSaving(false);
    }
  };

  // Send all three roles every time. The handler treats an omitted role as
  // "keep existing", so a partial payload would silently strand whichever role
  // the admin had just cleared back to its old value.
  const handleSaveColors = () =>
    submitColorChange(
      documentColors,
      'Document colours updated',
      'Failed to update document colours',
    );

  // Empty string per role clears it, so each template falls back to its own
  // default — NOT the same as saving #000000, which pins every role to black.
  // Derived from DOCUMENT_COLOR_ROLES rather than written out: the payload type
  // has optional properties, so a hardcoded literal would silently stop
  // clearing a role added to that list later, with no type error.
  const handleResetColors = () =>
    submitColorChange(
      Object.fromEntries(
        DOCUMENT_COLOR_ROLES.map((role) => [role.key, '']),
      ) as Record<DocumentColorRole, string>,
      'Document colours reverted to the per-template defaults',
      'Failed to reset document colours',
    );

  const handleSaveFeatures = async () => {
    if (!orgId) return;

    try {
      setSavingFeatures(true);
      setFeaturesError(null);
      
      await adminApi.updateOrganization(orgId, {
        enabledFeatures: selectedFeatures
      });

      // Refresh organization data
      const response = await adminApi.getOrganization(orgId);
      applyOrgResponse(response);

      setEditFeaturesOpen(false);
    } catch (err: any) {
      setFeaturesError(err.response?.data?.message || err.message || 'Failed to update features');
    } finally {
      setSavingFeatures(false);
    }
  };

  const handleDeleteOrganization = async () => {
    if (!orgId) return;

    try {
      setDeleting(true);
      setDeleteError(null);

      await adminApi.deleteOrganization(orgId);

      // Navigate back to organizations list on success
      navigate('/organizations');
    } catch (err: any) {
      setDeleteError(err.response?.data?.message || err.message || 'Failed to delete organization');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/organizations')} sx={{ mb: 2 }}>
          Back to Organizations
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!organization) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/organizations')} sx={{ mb: 2 }}>
          Back to Organizations
        </Button>
        <Alert severity="warning">Organization not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Button startIcon={<BackIcon />} onClick={() => navigate('/organizations')} sx={{ mb: 3 }}>
        Back to Organizations
      </Button>

      {/* Organization Info Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" fontWeight={700}>
              {organization.displayName || organization.name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={handleEditOrgDetailsClick}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => { setDeleteConfirmName(''); setDeleteError(null); setDeleteDialogOpen(true); }}
              >
                Delete
              </Button>
            </Box>
          </Box>
          
          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">ID</Typography>
              <Typography sx={{ fontFamily: 'monospace' }}>{organization.id}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Name</Typography>
              <Typography>{organization.name}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Domain</Typography>
              <Typography>{organization.domain || '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Monthly Run Limit</Typography>
              <Typography>{organization.maxRunsPerMonth || 50}</Typography>
            </Grid>
            {/* Doc Chat / Legal Research allowances — IA build only (Trello #380).
                On broker-AI credit billing is live (CREDIT_BILLING_ENABLED_STACKS
                = "broker-ai"), so both draw down the main credit balance and these
                separate per-month caps are dead: showing them implies an allowance
                that no longer gates anything. The IA stack still meters them
                separately, so the tiles stay there. */}
            {!IS_BROKER_ADMIN && (
              <>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">Doc Chat Limit</Typography>
                  <Typography>{organization.maxDocChatPerMonth || 50}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">Research Limit</Typography>
                  <Typography>{organization.maxResearchPerMonth || 50}</Typography>
                </Grid>
              </>
            )}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Run Pool Mode</Typography>
              <Chip
                label={organization.sharedRunPool !== false ? 'Shared Pool' : 'Per-User'}
                color={organization.sharedRunPool !== false ? 'primary' : 'default'}
                size="small"
                sx={{ mt: 0.5 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Two-Factor Auth (MFA)</Typography>
              <Chip
                label={organization.mfaRequired ? 'Required' : 'Optional'}
                color={organization.mfaRequired ? 'warning' : 'default'}
                size="small"
                sx={{ mt: 0.5 }}
              />
            </Grid>
            {/* Referral source — broker-AI distribution-partner tracking, shown
                only on the broker-AI admin build. (Trello #260) */}
            {IS_BROKER_ADMIN && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">Referral Source</Typography>
                <Box sx={{ mt: 0.5 }}>
                  {organization.referralSource ? (
                    <Chip label={organization.referralSource} color="info" size="small" />
                  ) : (
                    <Typography>-</Typography>
                  )}
                </Box>
              </Grid>
            )}
            {/* EFT payer marker (Trello #299; both builds since #664) — only
                rendered when set so the tile doesn't add noise for the common case. */}
            {organization.eftPayer === true && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">Payment Method</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip label="EFT payer" color="success" size="small" data-testid="eft-payer-chip" />
                </Box>
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Subscription & Credits (Trello #319) — what the org bought + how its
              credit balance is made up, so an admin never has to cross-reference
              Stitch. Fed by the `billing` summary on the org-detail endpoint;
              renders a placeholder until the backend deploys (backend ships
              first per cross-repo deploy ordering).
              Broker-admin visibility: SHARED (no IS_BROKER_ADMIN gate) — this
              page already shows billing-adjacent info (managed tier, EFT payer,
              invoices, billing edit) on both builds, broker admins are internal
              staff scoped to their own orgs by the backend, and the panel serves
              both stacks (org-tier-pool for broker, shared-pool for IA). */}
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Subscription &amp; Credits
          </Typography>
          {billing ? (
            <Grid container spacing={3} data-testid="billing-summary">
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">Billing Status</Typography>
                <Box sx={{ mt: 0.5 }}>
                  {(() => {
                    // Fall back to the neutral "none" meta if the backend ever
                    // ships a new billingStatus value ahead of this admin build,
                    // so an unknown status degrades instead of crashing the panel.
                    const statusMeta =
                      BILLING_STATUS_META[billing.billingStatus] ?? BILLING_STATUS_META.none;
                    return (
                      <Chip label={statusMeta.label} color={statusMeta.color} size="small" />
                    );
                  })()}
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">Subscription Package</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={billing.packageLabel} color="primary" size="small" />
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">Monthly Price</Typography>
                <Typography>{fmtRand(billing.priceZAR)}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">Next Billing / Renewal</Typography>
                <Typography>{fmtBillingDate(billing.nextBillingDate)}</Typography>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">Standard Monthly Credits</Typography>
                <Typography>{fmtCredits(billing.standardMonthlyCredits)}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Adjusted / Promo Credits
                </Typography>
                <Typography>
                  {billing.adjustedMonthlyCredits != null
                    ? fmtCredits(billing.adjustedMonthlyCredits)
                    : 'None'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Monthly Credits Remaining
                </Typography>
                <Typography>
                  {billing.monthlyCreditsRemaining != null
                    ? `${fmtCredits(billing.monthlyCreditsRemaining)}${
                        billing.monthlyCreditsAllocation != null
                          ? ` / ${fmtCredits(billing.monthlyCreditsAllocation)}`
                          : ''
                      }`
                    : '—'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">Top-up Credits Remaining</Typography>
                <Typography>{fmtCredits(billing.topUpCreditsRemaining)}</Typography>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">Subscription Start</Typography>
                <Typography>{fmtBillingDate(billing.subscriptionStartDate)}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">Total Credits Remaining</Typography>
                <Typography>{fmtCredits(billing.totalCreditsRemaining)}</Typography>
              </Grid>
            </Grid>
          ) : (
            <Typography color="text.secondary" data-testid="billing-summary-empty">
              No subscription or billing information available for this organisation.
            </Typography>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Account Manager Section */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              Account Manager
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={handleEditAccountManagerClick}
            >
              Edit
            </Button>
          </Box>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary">Name</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <PersonIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                <Typography>{organization.accountManagerName || '-'}</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary">Email</Typography>
              <Typography sx={{ mt: 0.5 }}>
                {organization.accountManagerEmail ? (
                  <a href={`mailto:${organization.accountManagerEmail}`} style={{ color: 'inherit' }}>
                    {organization.accountManagerEmail}
                  </a>
                ) : '-'}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Branding — a state-first checklist beside a live document preview
              (Trello #628). The card used to append each new asset as its own
              heading + button + paragraph of help, so four assets read as four
              unrelated widgets and the two Choose buttons floated top-right,
              detached from the logos they act on.

              Now: one row per asset showing whether it is set, one editor open
              at a time, and a miniature of the resulting document alongside —
              which replaces most of the help text, because the effect of a
              colour is visible rather than described. */}
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Branding
                </Typography>
                <Chip
                  size="small"
                  label={`${brandingSetCount} of 4 set`}
                  color={brandingSetCount === 4 ? 'success' : 'default'}
                  variant={brandingSetCount === 4 ? 'filled' : 'outlined'}
                  data-testid="branding-set-count"
                />
              </Box>

              {brandingError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBrandingError(null)}>
                  {brandingError}
                </Alert>
              )}
              {brandingSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setBrandingSuccess(null)}>
                  {brandingSuccess}
                </Alert>
              )}
              {emailBrandingError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEmailBrandingError(null)}>
                  {emailBrandingError}
                </Alert>
              )}
              {emailBrandingSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setEmailBrandingSuccess(null)}>
                  {emailBrandingSuccess}
                </Alert>
              )}

              {/* ---------- Document logo ---------- */}
              <BrandingRow
                testId="branding-row-logo"
                name="Document logo"
                detail={
                  organization.branding?.logoUrl
                    ? `Custom · ${BRANDING_IMAGE_DIMENSIONS.width} × ${BRANDING_IMAGE_DIMENSIONS.height} PNG`
                    : 'Using the platform default'
                }
                isSet={!!organization.branding?.logoUrl}
                open={brandingEditing === 'logo'}
                onToggle={() => setBrandingRow('logo')}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  PNG with a transparent background, max 5 MB, exactly{' '}
                  {BRANDING_IMAGE_DIMENSIONS.width} × {BRANDING_IMAGE_DIMENSIONS.height} px. Appears in
                  the header of generated DOCX outputs.
                </Typography>
                <Button variant="outlined" size="small" component="label" disabled={brandingUploading}>
                  {brandingPreviewFile ? 'Choose another' : 'Choose file'}
                  <input type="file" accept="image/png" hidden onChange={handleBrandingFileSelected} />
                </Button>

                {brandingPreviewUrl && brandingPreviewFile && (
                  <Box sx={{ mt: 2, border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden' }}>
                    <Box sx={{ bgcolor: '#ffffff', height: 90, px: '20px', display: 'flex', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
                      <Box component="img" src={brandingPreviewUrl} alt="Logo preview" sx={{ maxHeight: 70, maxWidth: '100%', objectFit: 'contain' }} />
                    </Box>
                    <Box sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'center', bgcolor: '#f8fafc' }}>
                      <Typography variant="caption" sx={{ color: '#64748b', mr: 'auto' }}>
                        {brandingPreviewFile.name} · {(brandingPreviewFile.size / 1024).toFixed(1)} KB
                      </Typography>
                      <Button size="small" onClick={handleCancelBrandingPreview} disabled={brandingUploading}>
                        Cancel
                      </Button>
                      <Button size="small" variant="contained" onClick={handleConfirmBrandingUpload} disabled={brandingUploading}>
                        {brandingUploading ? 'Saving…' : 'Save logo'}
                      </Button>
                    </Box>
                  </Box>
                )}
                {!brandingPreviewUrl && organization.branding?.logoDownloadUrl && (
                  <Box sx={{ mt: 2, border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden' }}>
                    <Box sx={{ bgcolor: '#ffffff', height: 90, px: '20px', display: 'flex', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
                      <Box component="img" src={organization.branding.logoDownloadUrl} alt="Current document header logo" sx={{ maxHeight: 70, maxWidth: '100%', objectFit: 'contain' }} />
                    </Box>
                    <Box sx={{ p: 1.5, bgcolor: '#f8fafc' }}>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        {`${orgId}/branding/document-header.png`}
                        {organization.branding.logoSizeBytes !== undefined &&
                          ` · ${(organization.branding.logoSizeBytes / 1024).toFixed(1)} KB`}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </BrandingRow>

              {/* ---------- Email logo ----------
                  Separate S3 key from the document header per the 2026-04-20
                  schema split; renders on a dark #111111 bar in outbound
                  completion emails (templates/base-layout-broker-ai.mjml). */}
              <BrandingRow
                testId="branding-row-email-logo"
                name="Email logo"
                detail={
                  organization.branding?.emailLogoDownloadUrl
                    ? `Custom · ${EMAIL_BRANDING_IMAGE_DIMENSIONS.width} × ${EMAIL_BRANDING_IMAGE_DIMENSIONS.height} PNG`
                    : 'Using the platform default'
                }
                isSet={!!organization.branding?.emailLogoDownloadUrl}
                open={brandingEditing === 'email'}
                onToggle={() => setBrandingRow('email')}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  PNG, max 5 MB, exactly {EMAIL_BRANDING_IMAGE_DIMENSIONS.width} ×{' '}
                  {EMAIL_BRANDING_IMAGE_DIMENSIONS.height} px. Renders at 38 px on a dark{' '}
                  <code>#111111</code> bar in completion emails — use a light or white logo for
                  contrast.
                </Typography>
                <Button variant="outlined" size="small" component="label" disabled={emailBrandingUploading}>
                  {emailBrandingPreviewFile ? 'Choose another' : 'Choose file'}
                  <input type="file" accept="image/png" hidden onChange={handleEmailBrandingFileSelected} />
                </Button>

                {emailBrandingPreviewUrl && emailBrandingPreviewFile && (
                  <Box sx={{ mt: 2, border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden' }}>
                    <Box sx={{ bgcolor: '#111111', height: 90, px: '20px', display: 'flex', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
                      <Box component="img" src={emailBrandingPreviewUrl} alt="Email logo preview" sx={{ height: 38, maxWidth: '100%', objectFit: 'contain' }} />
                    </Box>
                    <Box sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'center', bgcolor: '#f8fafc' }}>
                      <Typography variant="caption" sx={{ color: '#64748b', mr: 'auto' }}>
                        {emailBrandingPreviewFile.name} · {(emailBrandingPreviewFile.size / 1024).toFixed(1)} KB
                      </Typography>
                      <Button size="small" onClick={handleCancelEmailBrandingPreview} disabled={emailBrandingUploading}>
                        Cancel
                      </Button>
                      <Button size="small" variant="contained" onClick={handleConfirmEmailBrandingUpload} disabled={emailBrandingUploading}>
                        {emailBrandingUploading ? 'Saving…' : 'Save email logo'}
                      </Button>
                    </Box>
                  </Box>
                )}
                {!emailBrandingPreviewUrl && organization.branding?.emailLogoDownloadUrl && (
                  <Box sx={{ mt: 2, border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden' }}>
                    <Box sx={{ bgcolor: '#111111', height: 90, px: '20px', display: 'flex', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
                      <Box component="img" src={organization.branding.emailLogoDownloadUrl} alt="Current email header logo" sx={{ height: 38, maxWidth: '100%', objectFit: 'contain' }} />
                    </Box>
                    <Box sx={{ p: 1.5, bgcolor: '#f8fafc' }}>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        {`${orgId}/branding/email-header.png`}
                        {organization.branding.emailLogoSizeBytes !== undefined &&
                          ` · ${(organization.branding.emailLogoSizeBytes / 1024).toFixed(1)} KB`}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </BrandingRow>

              {/* ---------- Document fonts ---------- */}
              <BrandingRow
                testId="branding-row-font"
                name="Document fonts"
                detail={
                  brandingFontsAreSet
                    ? resolvedFonts.heading === resolvedFonts.body
                      ? resolvedFonts.body
                      : `${resolvedFonts.heading} headings · ${resolvedFonts.body} body`
                    : `Default (${DEFAULT_DOCUMENT_FONT})`
                }
                isSet={brandingFontsAreSet}
                open={brandingEditing === 'font'}
                onToggle={() => setBrandingRow('font')}
              >
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {DOCUMENT_FONT_ROLES.map((role) => {
                    const stored = storedFonts[role.key];
                    const unsupported = !!stored && !isSupportedDocumentFont(stored);
                    return (
                      <FormControl
                        key={role.key}
                        size="small"
                        sx={{ minWidth: 240 }}
                        disabled={fontSaving}
                      >
                        <InputLabel id={`branding-font-${role.key}-label`}>{role.label}</InputLabel>
                        <Select
                          labelId={`branding-font-${role.key}-label`}
                          label={role.label}
                          inputProps={{ 'data-testid': `branding-font-${role.key}` }}
                          value={stored}
                          onChange={(e) => handleFontChange(role.key, String(e.target.value))}
                        >
                          <MenuItem value="">
                            {/* The heading role falls back to the BODY face, not to
                                the platform default — same chain as the backend, so
                                "Default" must not claim Calibri when the body face
                                is Arial.
                                …but only when that body face is one the backend will
                                actually honour. A retired value (the org still holds
                                "Leitura Sans Grot") is rejected on the body role too,
                                so naming it here would promise a face nobody gets —
                                the same property this row is built around, one level
                                down. (ai_review !207, defects.) */}
                            <em>
                              {role.key === 'heading' && bodyFaceIsUsable
                                ? `Same as body (${storedFonts.body})`
                                : `Default (${DEFAULT_DOCUMENT_FONT})`}
                            </em>
                          </MenuItem>
                          {/* An org can hold a font that is no longer offered — e.g.
                              the retired "Leitura Sans Grot", which matched no
                              installed family. Without a matching MenuItem, MUI
                              renders the Select blank and warns about an
                              out-of-range value, so the admin cannot tell whether a
                              font is set at all. Surface it as a disabled option
                              instead: the backend already rejects it and falls back
                              to the default, and re-selecting is the fix. */}
                          {unsupported && (
                            <MenuItem value={stored} disabled>
                              {stored} (no longer supported — falls back to {DEFAULT_DOCUMENT_FONT})
                            </MenuItem>
                          )}
                          {SUPPORTED_DOCUMENT_FONTS.map((font) => (
                            <MenuItem key={font} value={font}>
                              {font}
                            </MenuItem>
                          ))}
                        </Select>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75 }}>
                          {role.help}
                        </Typography>
                      </FormControl>
                    );
                  })}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                  Sub-headings use the heading face — they differ from headings by colour only. Pick
                  widely-supported faces so documents render consistently across Word, Google Docs
                  and LibreOffice.
                </Typography>
              </BrandingRow>

              {/* ---------- Document colours ---------- */}
              <BrandingRow
                testId="branding-row-colours"
                name="Document colours"
                detail={
                  brandingColoursAreSet ? (
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                      {DOCUMENT_COLOR_ROLES.map((role) => (
                        <Box
                          key={role.key}
                          component="span"
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '3px',
                            border: '1px solid rgba(0,0,0,0.18)',
                            // Same emotion-stylesheet route as the preview, so
                            // the same guard — see toSafeDocumentColor.
                            bgcolor: toSafeDocumentColor(documentColors[role.key]),
                          }}
                        />
                      ))}
                      <Box component="span">Custom scheme</Box>
                    </Box>
                  ) : (
                    'Each template uses its own colours'
                  )
                }
                isSet={brandingColoursAreSet}
                open={brandingEditing === 'colours'}
                onToggle={() => setBrandingRow('colours')}
              >
                {DOCUMENT_COLOR_ROLES.map((role) => (
                  <Box key={role.key} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
                    <Box
                      component="input"
                      type="color"
                      aria-label={role.label}
                      data-testid={`document-color-${role.key}`}
                      value={documentColors[role.key]}
                      disabled={colorsSaving}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setColorDraft({ ...documentColors, [role.key]: e.target.value })
                      }
                      sx={{
                        width: 34,
                        height: 28,
                        p: 0,
                        flex: 'none',
                        border: '1px solid rgba(0,0,0,0.23)',
                        borderRadius: 1,
                        background: 'none',
                        cursor: colorsSaving ? 'default' : 'pointer',
                      }}
                    />
                    <Box>
                      <Typography variant="body2">{role.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {role.help}
                      </Typography>
                    </Box>
                  </Box>
                ))}
                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSaveColors}
                    disabled={colorsSaving || colorDraft === null}
                  >
                    {colorsSaving ? 'Saving…' : 'Save colours'}
                  </Button>
                  <Button size="small" onClick={handleResetColors} disabled={colorsSaving}>
                    Reset to defaults
                  </Button>
                  {colorDraft !== null && !colorsSaving && (
                    <Button
                      size="small"
                      color="inherit"
                      data-testid="discard-colour-draft"
                      onClick={() => setColorDraft(null)}
                    >
                      Discard
                    </Button>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                  <strong>Reset to defaults</strong> clears the override so each template uses its own
                  colours — different from picking {DEFAULT_DOCUMENT_COLOR.toUpperCase()}, which pins
                  every role to black.
                </Typography>
              </BrandingRow>
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <DocumentStylePreview
                heading={documentColors.heading}
                subheading={documentColors.subheading}
                body={documentColors.body}
                highlight={documentColors.highlight}
                headingFont={resolvedFonts.heading}
                font={resolvedFonts.body}
                logoUrl={organization.branding?.logoDownloadUrl}
              />
            </Grid>
          </Grid>



          {/* Billing Information moved into the consolidated "Billing & Plan"
              card below (Trello #332). */}
        </CardContent>
      </Card>

      {/* Usage Stats and History Chart */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Usage & Cost History (Last 3 Months)
              </Typography>
              <Box sx={{ height: 250, mt: 2 }}>
                {usageHistory.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={usageChartRows}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis yAxisId="left" orientation="left" stroke="#3182ce" />
                      <YAxis yAxisId="right" orientation="right" stroke="#e53e3e" />
                      <Tooltip
                        formatter={(value: any, name: string) => {
                          if (name === 'Total Cost (USD)') return [`$${Number(value).toFixed(2)}`, name];
                          return [value, name];
                        }}
                      />
                      {/* Doc-Assist-excluded, so the bar agrees with the "Completed Jobs"
                          stat beside it (#372); questions get their own bar when reported. */}
                      <Bar yAxisId="left" dataKey="completedJobs" name="Completed Jobs" fill="#3182ce" radius={[4, 4, 0, 0]} />
                      {showDocAssistStat && (
                        <Bar yAxisId="left" dataKey="docAssistQuestions" name="Doc Assist Questions" fill={DOC_ASSIST_COLOR} radius={[4, 4, 0, 0]} />
                      )}
                      <Bar yAxisId="right" dataKey="totalCostUsd" name="Total Cost (USD)" fill="#e53e3e" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography color="text.secondary">No usage data</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Current Period Stats
              </Typography>
              {currentUsage ? (
                <Box sx={{ mt: 3 }}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      ESTIMATED COST
                    </Typography>
                    <Typography variant="h4" color="primary.main" fontWeight={700}>
                      ${(currentUsage.totalCostUsd || 0).toFixed(2)}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      {/* #372 — renamed from "Submitted": this counter IS the credit
                          pool draw-down (fractional, e.g. 0.1 per Doc Assist question),
                          not a count of submissions. */}
                      <Typography variant="caption" color="text.secondary">Credits Used</Typography>
                      <Typography variant="h6" data-testid="stat-credits-used">{currentUsage.submittedCount}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">Completed Jobs</Typography>
                      <Typography variant="h6" color="success.main" data-testid="stat-completed-jobs">{resolveCompletedJobs(currentUsage)}</Typography>
                    </Grid>
                    {showDocAssistStat && (
                      <Grid size={{ xs: 6 }} data-testid="stat-doc-assist-questions">
                        <Typography variant="caption" color="text.secondary">Doc Assist Questions</Typography>
                        <Typography variant="h6" sx={{ color: DOC_ASSIST_COLOR }}>{resolveDocAssistCount(currentUsage)}</Typography>
                      </Grid>
                    )}
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">Failed</Typography>
                      <Typography variant="h6" color="error.main">{currentUsage.failedCount}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">Period</Typography>
                      <Typography variant="body1" fontWeight={500}>{currentUsage.period}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', py: 4 }}>
                  <Typography color="text.secondary">No current period data</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Features Configuration */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Features Configuration
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {(organization?.enabledFeatures || organization?.features || []).length} of {allFeatures.length} features enabled
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEditFeaturesClick}
            >
              Edit Features
            </Button>
          </Box>

          {Object.entries(featuresByCategory).map(([category, features]) => {
            const categoryKey = category as FeatureCategory;
            const enabledFeatureIds = new Set(organization?.enabledFeatures || organization?.features || []);
            const enabledCount = features.filter(f => enabledFeatureIds.has(String(f.id))).length;

            return (
              <Accordion key={category}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {categoryLabels[categoryKey] || category}
                    </Typography>
                    <Chip
                      label={`${enabledCount}/${features.length}`}
                      size="small"
                      color={enabledCount === features.length ? 'success' : enabledCount > 0 ? 'primary' : 'default'}
                      variant="outlined"
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {features.map((feature) => {
                      const isEnabled = enabledFeatureIds.has(String(feature.id));
                      return (
                        <Box
                          key={String(feature.id)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1.5,
                            borderRadius: 1,
                            bgcolor: isEnabled ? 'success.50' : 'grey.50',
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {feature.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {feature.description}
                            </Typography>
                          </Box>
                          <Chip
                            label={isEnabled ? 'Enabled' : 'Disabled'}
                            size="small"
                            color={isEnabled ? 'success' : 'default'}
                            variant={isEnabled ? 'filled' : 'outlined'}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </CardContent>
      </Card>

      {/* ═══ Consolidated Billing & Plan (Trello #332 redesign) ═══
          Replaces the old Billing Information subsection + Managed-plan card +
          Owner/payment-link card + Org-Credit-Pool card, which scattered one
          org's billing across four surfaces (EFT toggle two dialogs away from
          the amount it controls, "tier" set in four places, no single view of
          how the org is invoiced). Mode-FIRST: the billing mode is shown +
          selected up top and only that mode's controls appear, with a live
          "invoiced as" line. Every control reuses the existing handlers. Renders
          on BOTH builds; the mode set + controls differ by build. */}
      <Card sx={{ mb: 3 }} data-testid="billing-plan-section">
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h6" fontWeight={600}>Billing &amp; Plan</Typography>
              <Typography variant="caption" color="text.secondary">
                Everything that drives this organisation&apos;s invoice, in one place.
              </Typography>
            </Box>
            <Chip
              label={`Mode: ${BILLING_MODE_META[currentBillingMode].label}`}
              color={currentBillingMode === 'managed' || currentBillingMode === 'iacomp' ? 'secondary' : 'success'}
              size="small"
              data-testid="current-billing-mode"
            />
          </Box>

          {/* Live "how the next invoice is built" line — the single fact the old UI never surfaced. */}
          <Alert
            severity={currentBillingMode === 'seat' && !billingCompleteForInvoice ? 'warning' : 'info'}
            icon={<ReceiptIcon fontSize="inherit" />}
            sx={{ mb: 2 }}
            data-testid="invoiced-as"
          >
            <Typography variant="body2"><strong>Invoiced as:</strong> {invoicedAsSummary}</Typography>
          </Alert>

          {/* ── Direct-set selector (Trello #332; extended to the IA build by #664) ──
              Picking a method IS the switch (via a confirm) — no preview limbo, and
              only the active method's fields show below. */}
          <Box sx={{ mb: 2 }} data-testid="billing-method-selector">
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              How is this org billed?
            </Typography>
            <ToggleButtonGroup exclusive value={currentBillingMode} size="small" sx={{ mb: 1, flexWrap: 'wrap' }}>
              {BILLING_METHODS.map((m) => (
                  <ToggleButton
                    key={m}
                    value={m}
                    onClick={() => { if (m !== currentBillingMode) openMethodConfirm(m); }}
                    data-testid={`method-${m}`}
                    sx={{ textTransform: 'none', px: 2.5 }}
                  >
                  {BILLING_MODE_META[m].selectorLabel}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {BILLING_MODE_META[currentBillingMode].blurb}
            </Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* ── Organisation owner (ALL broker modes) ──
              The owner is the org's designated manager: on self-serve they're the
              Stitch billing target, and across EVERY mode they're the user who can
              manage the organisation (add members, etc.). This used to live inside
              the self-serve panel only, so EFT / Comp'd orgs had no way to set it —
              Marcus flagged that putting an org on EFT hid the owner picker and
              blocked adding users (Trello #332).

              Rendered on BOTH builds since Trello #664: the owner is who the EFT
              invoice and its payment reminders are emailed to, so hiding this on
              the IA build left every IA org with no billing contact at all
              (ownerEmail was set on 0 of 71 IA orgs) — the invoice would have
              reached only the account manager. Not gated on billing mode. */}
          <Box sx={{ mb: 2 }} data-testid="org-owner-section">
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>Organisation owner</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {organization?.ownerEmail
                  ? `Current owner: ${organization.ownerEmail}. `
                  : 'No owner set — pick one of this org’s users. '}
                The owner manages the organisation (adding users, etc.) and, on self-serve, is billed via Stitch.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                <FormControl size="small" sx={{ minWidth: 320 }} disabled={members.length === 0}>
                  <InputLabel id="owner-user-label">Owner</InputLabel>
                  <Select
                    labelId="owner-user-label"
                    label="Owner"
                    value={selectedOwnerUserId}
                    onChange={(e) => setSelectedOwnerUserId(e.target.value as string)}
                  >
                    {members.map((m) => {
                      const name = `${m.firstName || ''} ${m.lastName || ''}`.trim();
                      return (
                        <MenuItem key={m.userId} value={m.userId}>
                          {name ? `${name} — ${m.email}` : m.email}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  onClick={handleSaveOwner}
                  disabled={savingOwner || !selectedOwnerUserId || selectedOwnerUserId === organization?.ownerUserId}
                >
                  {savingOwner ? 'Saving…' : 'Save owner'}
                </Button>
              </Box>
              {members.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  This org has no members yet — add a user before setting an owner.
                </Typography>
              )}
              {ownerSuccess && <Alert severity="success" sx={{ mt: 2 }}>{ownerSuccess}</Alert>}
              {ownerError && <Alert severity="error" sx={{ mt: 2 }}>{ownerError}</Alert>}
              <Divider sx={{ mt: 2 }} />
          </Box>

          {/* ── EFT flat — fields only; the selector above sets the mode ── */}
          {currentBillingMode === 'eft' && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>EFT payer</strong> — invoiced the flat Tier + Monthly amount from Billing details below,
                by direct bank transfer. Counts toward FSP-Hub commission reporting.
              </Typography>
            </Box>
          )}

          {/* ── Comp'd / Managed (broker) ── */}
          {currentBillingMode === 'managed' && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 240 }}>
                  <InputLabel id="managed-tier-label">Managed tier</InputLabel>
                  <Select
                    labelId="managed-tier-label"
                    label="Managed tier"
                    value={managedTier}
                    onChange={(e) => setManagedTier(e.target.value as ManagedPlanTier)}
                  >
                    {tierRunsMenuItems(MANAGED_TIER_CODES_ORDERED)}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => { setManagedError(null); setManagedSuccess(null); setManagedConfirmOpen(true); }}
                >
                  {organization?.billingMode === 'managed' ? 'Update managed plan' : 'Activate managed plan'}
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                Writes an active, no-charge subscription at the chosen tier, hides the payment page for this
                org&apos;s users, and enables the shared credit pool. Use the selector above to switch the org
                to another billing method.
              </Typography>
              {managedSuccess && <Alert severity="success" sx={{ mt: 2 }}>{managedSuccess}</Alert>}
              {managedError && <Alert severity="error" sx={{ mt: 2 }}>{managedError}</Alert>}
            </Box>
          )}

          {/* ── Self-serve (broker): Stitch payment link (owner picker is the shared section above) ── */}
          {currentBillingMode === 'selfserve' && (
            <Box sx={{ mb: 2 }}>
              {/* Switching INTO self-serve is handled by the selector above; this panel
                  shows only the self-serve-specific fields. */}
              {/* The owner picker now lives in the shared "Organisation owner"
                  section above (shown for every broker mode). This panel keeps only
                  the self-serve-specific Stitch payment link, which bills that owner. */}
              {organization?.billingMode === 'managed' ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                  This org is on a managed (comp&apos;d) plan — no payment link needed.
                </Typography>
              ) : (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Generate payment link</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Creates a Stitch recurring-subscription link at the chosen tier and bills the owner above.
                    Send it to the owner — they capture a card without logging in. FSP-Hub-referred orgs get
                    the discounted price automatically.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 240 }}>
                      <InputLabel id="payment-link-tier-label">Tier</InputLabel>
                      <Select
                        labelId="payment-link-tier-label"
                        label="Tier"
                        value={paymentLinkTier}
                        onChange={(e) => setPaymentLinkTier(e.target.value as 'bronze' | 'silver' | 'gold')}
                      >
                        {tierRunsMenuItems()}
                      </Select>
                    </FormControl>
                    <Button
                      variant="contained"
                      onClick={handleGeneratePaymentLink}
                      disabled={generatingLink || !organization?.ownerUserId}
                    >
                      {generatingLink ? 'Generating…' : 'Generate payment link'}
                    </Button>
                  </Box>
                  {!organization?.ownerUserId && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Set an owner first — the link bills the owner.
                    </Typography>
                  )}
                  {paymentLinkUrl && (
                    <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={paymentLinkUrl}
                        InputProps={{ readOnly: true }}
                        sx={{ minWidth: 320, flex: 1 }}
                        data-testid="payment-link-url"
                      />
                      <Button variant="outlined" onClick={handleCopyPaymentLink}>
                        {linkCopied ? 'Copied' : 'Copy'}
                      </Button>
                    </Box>
                  )}
                  {paymentLinkError && <Alert severity="error" sx={{ mt: 2 }}>{paymentLinkError}</Alert>}
                </Box>
              )}
            </Box>
          )}

          {/* Per-seat is intentionally NOT a selectable mode chip (it's never
              auto-derived + has no distinct switch action — see BILLING_MODE_META
              in organization-billing-mode.ts). Orgs whose members each pay their own
              seat are configured via the per-member tier dropdowns in the Members
              table below; no mode switch is involved. */}

          {/* ── Manual / per-license (IA) ── */}
          {currentBillingMode === 'manual' && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Per-license billing, invoiced by finance. Set the cost per license under Billing details below;
              the monthly total is cost × active members. Billing status is set in the Organization Details editor.
            </Typography>
          )}

          {/* ── Comp'd (IA) ── */}
          {currentBillingMode === 'iacomp' && (
            <Alert severity="info" sx={{ mb: 2 }}>This org is comp&apos;d — no invoices are generated.</Alert>
          )}

          <Divider sx={{ mb: 2 }} />

          {/* ── Billing details (shared — the fields every invoice needs) ── */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={600}>Billing details</Typography>
            <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={handleEditBillingClick}>
              Edit billing details
            </Button>
          </Box>

          {/* Tell the admin an invoice CANNOT be generated yet, and exactly which
              field is missing, right here next to the Edit button — rather than
              only after they scroll to Monthly Invoices and find Generate dead
              behind a generic warning (Trello #360). */}
          {missingBillingFields.length > 0 && (
            <Alert
              severity="warning"
              data-testid="billing-details-incomplete"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={handleEditBillingClick}>
                  Add {missingBillingFields.length === 1 ? 'it' : 'them'}
                </Button>
              }
            >
              <Typography variant="body2">
                No invoice can be generated for this organisation yet — it still needs a{' '}
                <strong>{formatMissingFields(missingBillingFields)}</strong>.
              </Typography>
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary">Billing Name</Typography>
              <Typography sx={{ mt: 0.5 }}>{organization.billingName || '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary">VAT Number</Typography>
              <Typography sx={{ mt: 0.5 }}>{organization.vatNumber || '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary">Subscription Tier</Typography>
              <Typography sx={{ mt: 0.5 }}>{organization.tierName || '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary">Monthly Subscription (ZAR)</Typography>
              <Typography sx={{ mt: 0.5, fontWeight: 600, color: 'primary.main' }}>
                {typeof organization.monthlyAmountZAR === 'number' ? `R ${organization.monthlyAmountZAR.toFixed(2)}` : '-'}
              </Typography>
            </Grid>
            {!IS_BROKER_ADMIN && (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary">Number of Licenses</Typography>
                  <Typography sx={{ mt: 0.5, fontWeight: 600 }}>
                    {memberCount > 0 ? `${memberCount} ${memberCount === 1 ? 'user' : 'users'}` : '-'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary">Cost per License (ZAR)</Typography>
                  <Typography sx={{ mt: 0.5 }}>
                    {organization.costPerLicenseZAR ? `R ${organization.costPerLicenseZAR.toFixed(2)}` : '-'}
                  </Typography>
                </Grid>
                {monthlyLicenseTotal !== null && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary">Monthly Total (ZAR)</Typography>
                    <Typography sx={{ mt: 0.5, fontWeight: 600, color: 'primary.main' }}>
                      R {monthlyLicenseTotal.toFixed(2)}
                    </Typography>
                  </Grid>
                )}
              </>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary">Invoice recipient</Typography>
              <Typography sx={{ mt: 0.5 }}>{organization.accountManagerEmail || '— (set an Account Manager)'}</Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="caption" color="text.secondary">Billing Address</Typography>
              <Typography sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>{organization.billingAddress || '-'}</Typography>
            </Grid>
            {/* The description line printed on this org's invoices (Trello #670).
                Blank falls back to the brand default, so show that rather than a
                dash — an admin reading "-" would reasonably think the invoice
                had no description row at all. */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="caption" color="text.secondary">Invoice Description</Typography>
              <Typography
                data-testid="billing-invoice-description"
                sx={{ mt: 0.5, whiteSpace: 'pre-line', ...(organization.invoiceDescription ? {} : { color: 'text.secondary', fontStyle: 'italic' }) }}
              >
                {organization.invoiceDescription || `${defaultInvoiceDescriptionLabel} — <billing month>`}
              </Typography>
            </Grid>
          </Grid>

          {/* ── Org credit pool reset (only once ORG_CREDIT_POOL is enabled) ── */}
          {isPoolEnrolled && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>Org credit pool</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Shared per-org run pool (ORG_CREDIT_POOL enabled). Reset the counter once at enrollment.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<RefreshIcon />}
                  onClick={() => { setPoolResetError(null); setPoolResetSuccess(null); setPoolResetConfirmOpen(true); }}
                >
                  Reset Pool Counter
                </Button>
              </Box>
              {poolResetSuccess && <Alert severity="success" sx={{ mt: 2 }}>{poolResetSuccess}</Alert>}
              {poolResetError && <Alert severity="error" sx={{ mt: 2 }}>{poolResetError}</Alert>}
            </>
          )}
        </CardContent>
      </Card>

      {/* Owner/payment-link + Org-Credit-Pool now live inside the consolidated
          "Billing & Plan" card above (Trello #332). */}

      <Dialog open={poolResetConfirmOpen} onClose={() => !poolResetting && setPoolResetConfirmOpen(false)}>
        <DialogTitle>Reset pool counter?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This zeroes <strong>{organization?.displayName || organization?.name || orgId}</strong>'s shared
            run counter for the current billing period. Members' usage history is unaffected; only the pool's
            current-period count is reset. Intended as a one-off enrollment step.
          </Typography>
          {poolResetError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {poolResetError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPoolResetConfirmOpen(false)} disabled={poolResetting}>
            Cancel
          </Button>
          <Button onClick={handleResetPoolCounter} color="warning" variant="contained" disabled={poolResetting}>
            {poolResetting ? 'Resetting…' : 'Reset counter'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={managedConfirmOpen} onClose={() => !activatingManaged && setManagedConfirmOpen(false)}>
        <DialogTitle>Activate managed (comp&apos;d) plan?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This comps <strong>{organization?.displayName || organization?.name || orgId}</strong> on the{' '}
            <strong>{TIER_CATALOGUE[managedTier].displayName}</strong> tier
            ({TIER_CATALOGUE[managedTier].monthlyRunsQuota} runs/mo). We pay — the org sees no payment page and
            no card is charged. You can switch it back to self-serve later via the &ldquo;Switch to self-serve&rdquo; button.
          </Typography>
          {managedError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {managedError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManagedConfirmOpen(false)} disabled={activatingManaged}>
            Cancel
          </Button>
          <Button onClick={handleActivateManaged} color="success" variant="contained" disabled={activatingManaged}>
            {activatingManaged ? 'Activating…' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Billing-method switch confirm (direct-set selector). Applying
          sets the chosen method AND clears the conflicting flags, so an org can never
          be both managed and eftPayer (the Alwyn-Bamberger bug). */}
      <Dialog open={methodConfirm !== null} onClose={() => !switchingMethod && setMethodConfirm(null)} data-testid="method-confirm-dialog">
        <DialogTitle>Switch billing method?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Switch <strong>{organization?.displayName || organization?.name || orgId}</strong> to{' '}
            <strong>{methodConfirm ? BILLING_MODE_META[methodConfirm].selectorLabel : ''}</strong>?{' '}
            {methodConfirm ? BILLING_MODE_META[methodConfirm].confirmEffect : ''}
          </Typography>
          {methodConfirm === 'managed' && (
            <FormControl size="small" sx={{ mt: 2, minWidth: 240 }}>
              <InputLabel id="method-managed-tier-label">Managed tier</InputLabel>
              <Select
                labelId="method-managed-tier-label"
                label="Managed tier"
                value={methodConfirmTier}
                onChange={(e) => setMethodConfirmTier(e.target.value as ManagedPlanTier)}
              >
                {tierRunsMenuItems(MANAGED_TIER_CODES_ORDERED)}
              </Select>
            </FormControl>
          )}
          {methodError && <Alert severity="error" sx={{ mt: 2 }}>{methodError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setMethodConfirm(null); setMethodError(null); }} disabled={switchingMethod}>Cancel</Button>
          <Button onClick={applyBillingMethod} variant="contained" disabled={switchingMethod} data-testid="confirm-method-switch">
            {switchingMethod ? 'Switching…' : 'Switch'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Job History Export */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Job History Export
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Last 5 months
              </Typography>
            </Box>
            <Button 
              size="small" 
              startIcon={<RefreshIcon />}
              onClick={fetchJobExports}
              disabled={jobExportsLoading}
            >
              Refresh
            </Button>
          </Box>

          {/* Custom date range — total runs + downloadable report for any window (e.g. last 90 days) */}
          <Box
            sx={{
              mb: 3,
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'action.hover',
            }}
          >
            <Typography variant="subtitle2" fontWeight={600}>
              Custom date range
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              Total runs + a downloadable report for any window (e.g. the last 90 days).
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                label="From"
                type="date"
                size="small"
                value={customRangeFrom}
                onChange={(e) => setCustomRangeFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={customRangeExporting}
              />
              <TextField
                label="To"
                type="date"
                size="small"
                value={customRangeTo}
                onChange={(e) => setCustomRangeTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={customRangeExporting}
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleExportCustomRange}
                disabled={customRangeExporting || !customRangeFrom || !customRangeTo}
                startIcon={customRangeExporting ? <CircularProgress size={16} /> : <DownloadIcon />}
              >
                {customRangeExporting ? 'Generating…' : 'Generate export'}
              </Button>
            </Box>

            {customRangeError && (
              <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setCustomRangeError(null)}>
                {customRangeError}
              </Alert>
            )}

            {customRangeResult && (
              <Alert severity="success" sx={{ mt: 1.5 }}>
                <strong>{customRangeResult.jobCount}</strong> total run
                {customRangeResult.jobCount === 1 ? '' : 's'} between{' '}
                {new Date(customRangeResult.from).toLocaleDateString('en-GB', {
                  day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC',
                })}{' '}
                and{' '}
                {new Date(customRangeResult.to).toLocaleDateString('en-GB', {
                  day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC',
                })}
                . Report downloaded.
              </Alert>
            )}
          </Box>

          {jobExportsError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setJobExportsError(null)}>
              {jobExportsError}
            </Alert>
          )}

          {jobExportsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={40} />
            </Box>
          ) : jobExports.length === 0 ? (
            <Alert severity="info">No usage history available to generate exports</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Period</strong></TableCell>
                    <TableCell><strong>Export Number</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Generated</strong></TableCell>
                    {/* One column per export TYPE so every row's actions align
                        (the old single "Actions" cell wrapped inconsistently — some
                        rows one line, some two). */}
                    <TableCell align="right"><strong>Monthly report</strong></TableCell>
                    <TableCell align="right"><strong>Runs + cost</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobExports.map((exportItem) => {
                    const [year, month] = exportItem.period.split('-');
                    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                                       'July', 'August', 'September', 'October', 'November', 'December'];
                    const monthName = monthNames[parseInt(month) - 1];
                    const exportNumber = `JOB-${year}-${month}`;
                    const isGenerating = exportingJobPeriod === exportItem.period;

                    return (
                      <TableRow key={exportItem.period}>
                        <TableCell>
                          {monthName} {year}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DescriptionIcon fontSize="small" color="action" />
                            {exportNumber}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={exportItem.exists ? 'Generated' : 'Not Generated'}
                            color={exportItem.exists ? 'success' : 'default'}
                            size="small"
                            variant={exportItem.exists ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell>
                          {exportItem.generatedAt 
                            ? new Date(exportItem.generatedAt).toLocaleString()
                            : '-'
                          }
                        </TableCell>
                        {/* Monthly report export (the JOB-YYYY-MM artifact) — its own
                            column so it aligns across every row. */}
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                            {exportItem.exists && exportItem.url ? (
                              <>
                                <Button
                                  size="small"
                                  variant="text"
                                  startIcon={<DownloadIcon />}
                                  onClick={() => openDownloadInNewTab(exportItem.url)}
                                  sx={{ whiteSpace: 'nowrap' }}
                                >
                                  Download
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleExportJobs(exportItem.period)}
                                  disabled={isGenerating}
                                  startIcon={isGenerating ? <CircularProgress size={16} /> : undefined}
                                  sx={{ whiteSpace: 'nowrap' }}
                                >
                                  {isGenerating ? 'Regenerating...' : 'Regenerate'}
                                </Button>
                              </>
                            ) : (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleExportJobs(exportItem.period)}
                                disabled={isGenerating}
                                startIcon={isGenerating ? <CircularProgress size={16} /> : undefined}
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {isGenerating ? 'Generating...' : 'Generate'}
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                        {/* Runs+cost CSV — its own column. Shown on BOTH admin builds:
                            per-run cost is already visible in the Recent Activity Cost
                            column, so hiding the export would conceal nothing
                            (broker-admin-visibility-on-new-surface §1.5). The real access
                            boundary is the backend admin-auth + org-stack scope, not this
                            client-side render. */}
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="text"
                            startIcon={
                              exportingRunsPeriod === exportItem.period ? (
                                <CircularProgress size={16} />
                              ) : (
                                <DownloadIcon />
                              )
                            }
                            onClick={() => handleExportRunsCsv(exportItem.period)}
                            disabled={exportingRunsPeriod === exportItem.period}
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            {exportingRunsPeriod === exportItem.period
                              ? 'Preparing…'
                              : 'Runs + cost (CSV)'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Monthly Invoices */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Monthly Invoices
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Next month + last 5 months — subscriptions bill in advance, so invoice
                the month the client is paying for.
              </Typography>
            </Box>
            <Button 
              size="small" 
              startIcon={<RefreshIcon />}
              onClick={fetchInvoices}
              disabled={invoicesLoading}
            >
              Refresh
            </Button>
          </Box>

          {invoicesError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setInvoicesError(null)}>
              {invoicesError}
            </Alert>
          )}

          {invoiceSendSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInvoiceSendSuccess(null)}>
              {invoiceSendSuccess}
            </Alert>
          )}

          {invoiceSendError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setInvoiceSendError(null)}>
              {invoiceSendError}
            </Alert>
          )}

          {invoicesLoading && invoices.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : invoices.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Period</TableCell>
                    <TableCell>Invoice Number</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Generated</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow key={invoice.period} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          {formatPeriod(invoice.period)}
                          {isNextMonthPeriod(invoice.period) && (
                            <Chip
                              label="Upcoming"
                              size="small"
                              variant="outlined"
                              color="primary"
                              data-testid="upcoming-period-chip"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ReceiptIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {invoice.invoiceNumber}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={invoice.exists ? 'Generated' : 'Not Generated'} 
                          size="small" 
                          color={invoice.exists ? 'success' : 'default'}
                          variant={invoice.exists ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>
                        {invoice.generatedAt ? (
                          <Typography variant="caption">
                            {new Date(invoice.generatedAt).toLocaleDateString()} {new Date(invoice.generatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        ) : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          {invoice.exists && invoice.url && (
                            <Button
                              size="small"
                              startIcon={<DownloadIcon />}
                              onClick={() => openDownloadInNewTab(invoice.url)}
                            >
                              Download
                            </Button>
                          )}
                          {invoice.exists && (
                            <MuiTooltip
                              title={organization.accountManagerEmail
                                ? `Email this invoice to ${organization.accountManagerEmail}`
                                : 'Set an account manager email (in Account Manager details below) to send invoices'}
                            >
                              {/* span wrapper so the tooltip still shows when the button is disabled */}
                              <span>
                                <Button
                                  size="small"
                                  startIcon={<EmailIcon />}
                                  onClick={() => handleSendInvoice(invoice.period)}
                                  disabled={!organization.accountManagerEmail || invoiceSendingPeriod !== null}
                                >
                                  {invoiceSendingPeriod === invoice.period ? 'Sending…' : 'Send'}
                                </Button>
                              </span>
                            </MuiTooltip>
                          )}
                          {isGeneratableInvoicePeriod(invoice.period) && (
                            <Button
                              size="small"
                              variant={invoice.exists ? 'outlined' : 'contained'}
                              onClick={() => handleGenerateInvoice(invoice.period)}
                              disabled={invoiceGenerating}
                            >
                              {invoice.exists ? 'Regenerate' : 'Generate'}
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="text.secondary">No invoice data available</Typography>
          )}
        </CardContent>
      </Card>

      {/* Runs — live job history for the org (OrganizationIndex GSI) */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              Runs
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel id="org-runs-status-filter-label">Status</InputLabel>
                <Select
                  labelId="org-runs-status-filter-label"
                  label="Status"
                  value={jobsStatusFilter}
                  onChange={(e) => {
                    setJobsStatusFilter(e.target.value as typeof jobsStatusFilter);
                    setJobsPage(0); // new filter → back to the first page
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                  <MenuItem value="FAILED">Failed</MenuItem>
                  <MenuItem value="PROCESSING">Processing</MenuItem>
                  <MenuItem value="QUEUED">Queued</MenuItem>
                </Select>
              </FormControl>
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={handleRefreshJobs}
                disabled={jobsLoading}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {jobsError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setJobsError(null)}>
              {jobsError}
            </Alert>
          )}

          {jobsLoading && orgJobs.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <JobRunsTable
                jobs={orgJobs}
                showUser
                onUserClick={(uid) => navigate(`/users/${uid}`)}
                emptyMessage={jobsStatusFilter ? `No ${jobsStatusFilter.toLowerCase()} runs found` : 'No runs found for this organization yet'}
              />

              {jobsTotal > 0 && (
                <TablePagination
                  component="div"
                  count={jobsTotal}
                  page={jobsPage}
                  onPageChange={(_, newPage) => setJobsPage(newPage)}
                  rowsPerPage={ADMIN_RUNS_PER_PAGE}
                  rowsPerPageOptions={[10]}
                  sx={{ borderTop: 1, borderColor: 'divider' }}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Members */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              Members
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button 
                size="small" 
                startIcon={<RefreshIcon />}
                onClick={handleRefreshMembers}
                disabled={membersLoading}
              >
                Refresh
              </Button>
              <Chip
                label={
                  debouncedMemberSearch
                    ? `${members.length} matching`
                    : `${members.length} members`
                }
                size="small"
              />
            </Box>
          </Box>

          {/* Server-side: the member list is paginated ("Load more"), so a
              filter over the loaded rows would silently miss anyone on a later
              page. The backend searches the whole organisation (Trello #607). */}
          <TextField
            size="small"
            fullWidth
            placeholder="Search members by name or email..."
            value={memberSearch}
            onChange={(e) => setMemberSearch(e.target.value)}
            sx={{ mb: 2, maxWidth: 420 }}
            inputProps={{ 'data-testid': 'member-search-input' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {membersSearchTruncated && (
            <Alert severity="warning" sx={{ mb: 2 }} data-testid="member-search-truncated">
              This organisation has more members than one search pass scans, so there may be
              further matches below. Narrow the term to be sure you&apos;ve seen them all.
            </Alert>
          )}

          {membersError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMembersError(null)}>
              {membersError}
            </Alert>
          )}

          {memberTierError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMemberTierError(null)}>
              {memberTierError}
            </Alert>
          )}

          {membersLoading && members.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : members.length > 0 ? (
            <>
              {/* Dimmed while a search re-fetches so stale rows read as stale,
                  matching the Inactive Users table. */}
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ opacity: membersLoading ? 0.5 : 1 }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Paid</TableCell>
                      {isBrokerAi && <TableCell>Tier</TableCell>}
                      <TableCell>Jobs (Month)</TableCell>
                      <TableCell>Joined</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {members.map((member) => (
                      <TableRow
                        key={member.userId}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/users/${member.userId}`)}
                      >
                        <TableCell>{member.email}</TableCell>
                        <TableCell>
                          {member.firstName || member.lastName 
                            ? `${member.firstName || ''} ${member.lastName || ''}`.trim()
                            : '-'
                          }
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={member.isEnabled ? 'Active' : 'Disabled'} 
                            size="small" 
                            color={member.isEnabled ? 'success' : 'error'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={member.isPaid ? 'Yes' : 'No'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        {isBrokerAi && (
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <FormControl
                              size="small"
                              sx={{ minWidth: 110 }}
                              disabled={savingTierForUserId === member.userId}
                            >
                              <Select
                                value={(member.tier as TierSelectValue) || ''}
                                displayEmpty
                                onChange={(e) =>
                                  handleMemberTierChange(
                                    member.userId,
                                    e.target.value as TierSelectValue,
                                  )
                                }
                              >
                                <MenuItem value="">
                                  <em>None</em>
                                </MenuItem>
                                {TIER_CODES_ORDERED.map((code) => (
                                  <MenuItem key={code} value={code}>
                                    {TIER_CATALOGUE[code].displayName}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                        )}
                        <TableCell>
                          {member.usage ? member.usage.submittedCount : '-'}
                        </TableCell>
                        <TableCell>{new Date(member.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Button 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/users/${member.userId}`);
                            }}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {hasMoreMembers && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Button 
                    onClick={handleLoadMoreMembers} 
                    disabled={membersLoading}
                  >
                    {membersLoading ? <CircularProgress size={20} /> : 'Load More Members'}
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <Typography color="text.secondary">
              {debouncedMemberSearch
                ? `No members match "${debouncedMemberSearch}".`
                : 'No members found'}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Edit Features Dialog */}
      <Dialog
        open={editFeaturesOpen}
        onClose={() => !savingFeatures && setEditFeaturesOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Organization Features</DialogTitle>
        <DialogContent>
          {featuresError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFeaturesError(null)}>
              {featuresError}
            </Alert>
          )}
          <FeatureSelector
            selectedFeatures={selectedFeatures}
            onChange={setSelectedFeatures}
            disabled={savingFeatures}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditFeaturesOpen(false)} disabled={savingFeatures}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveFeatures}
            disabled={savingFeatures}
            startIcon={savingFeatures ? <CircularProgress size={20} /> : null}
          >
            {savingFeatures ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Account Manager Dialog */}
      <Dialog
        open={editAccountManagerOpen}
        onClose={() => !savingAccountManager && setEditAccountManagerOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Account Manager</DialogTitle>
        <DialogContent>
          {accountManagerError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {accountManagerError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Our staff member who looks after this client — picked from the
                Admins group, never typed. A pre-existing free-text value stays
                selectable (see AccountManagerSelect) so opening this dialog on
                a legacy org and saving cannot silently clear it. */}
            <AccountManagerSelect
              labelId="edit-org-account-manager-label"
              valueEmail={accountManagerEmail}
              valueName={accountManagerName}
              onChange={(selection) => {
                setAccountManagerName(selection?.name || '');
                setAccountManagerEmail(selection?.email || '');
              }}
              disabled={savingAccountManager}
              helperText="The internal account manager for this organisation. They receive its run-limit alerts."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditAccountManagerOpen(false)} disabled={savingAccountManager}>
            Cancel
          </Button>
          <Button onClick={handleSaveAccountManager} variant="contained" disabled={savingAccountManager}>
            {savingAccountManager ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Billing Information Dialog */}
      <Dialog
        open={editBillingOpen}
        onClose={() => !savingBilling && setEditBillingOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Billing Information</DialogTitle>
        <DialogContent>
          {billingError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {billingError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Billing Name"
              value={billingName}
              onChange={(e) => setBillingName(e.target.value)}
              fullWidth
              placeholder="e.g., Intelligent Automation (Pty) Ltd"
              helperText="Name to appear on invoices"
            />
            {/* The backend refuses to generate an invoice without it, so mark it
                required here instead of letting the admin discover it at a dead
                Generate button (Trello #360). Required on BOTH builds since
                #670 — `resolveInvoiceBilling`'s `addressRequired` is
                `!isBrokerAi || isEftPayerOrg`, i.e. an IA org has ALWAYS needed
                one; only this dialog treated it as broker-only. */}
            <TextField
              label="Billing Address"
              value={billingAddress}
              onChange={(e) => setBillingAddress(e.target.value)}
              fullWidth
              multiline
              rows={3}
              required
              error={!billingAddress.trim()}
              placeholder="e.g., 43 Blaauwberg Road, Table View, 7441"
              helperText='Required — appears as the "Bill to" address on the invoice.'
            />
            <TextField
              label="VAT Number"
              value={billingVatNumber}
              onChange={(e) => setBillingVatNumber(e.target.value)}
              fullWidth
              placeholder="e.g., 4123456789"
              helperText="Optional: VAT/Tax registration number"
            />
            {/* Cost-per-license is an IA per-seat billing input — not applicable
                to broker-AI's flat monthly subscription (Trello #323). */}
            {!IS_BROKER_ADMIN && (
              <TextField
                label="Cost per License (ZAR)"
                type="number"
                value={billingCostPerLicense}
                onChange={(e) => setBillingCostPerLicense(e.target.value)}
                fullWidth
                placeholder="e.g., 250.00"
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Optional: Monthly cost per user license in South African Rand"
              />
            )}
            {/* Tier + amount are required on BOTH builds since Trello #670. The
                backend demands them of an IA org exactly as it does of a broker
                one, so leaving them optional here let an admin save an org that
                could never be invoiced — and the failure surfaced much later, as
                a 400 from Generate Invoice, in a different dialog. */}
            <TextField
              label="Subscription Tier Name"
              value={billingTierName}
              onChange={(e) => setBillingTierName(e.target.value)}
              fullWidth
              required
              error={!billingTierName.trim()}
              placeholder="e.g., Standard"
              helperText="Required — tier label shown under the invoice line item."
            />
            <TextField
              label="Monthly Subscription Amount (ZAR)"
              type="number"
              value={billingMonthlyAmount}
              onChange={(e) => setBillingMonthlyAmount(e.target.value)}
              fullWidth
              required
              error={!(parseFloat(billingMonthlyAmount) > 0)}
              placeholder="e.g., 1850.00"
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Required — flat monthly fee auto-pulled onto the invoice."
            />
            {/* The invoice's description row (Trello #670). Blank keeps the brand
                default, so the placeholder shows exactly what the document will
                say if this is left empty. */}
            <TextField
              label="Invoice Description"
              value={billingInvoiceDescription}
              onChange={(e) => setBillingInvoiceDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              inputProps={{ maxLength: INVOICE_DESCRIPTION_MAX_CHARS, 'data-testid': 'billing-invoice-description-input' }}
              placeholder={`${defaultInvoiceDescriptionLabel} — <billing month>`}
              helperText={`Optional — replaces the description line on this organisation's invoices, manual and automated. Leave blank for "${defaultInvoiceDescriptionLabel} — <billing month>". ${billingInvoiceDescription.length}/${INVOICE_DESCRIPTION_MAX_CHARS}`}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditBillingOpen(false)} disabled={savingBilling}>
            Cancel
          </Button>
          <Button onClick={handleSaveBilling} variant="contained" disabled={savingBilling}>
            {savingBilling ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Organization Details Dialog */}
      <Dialog
        open={editOrgDetailsOpen}
        onClose={() => !savingOrgDetails && setEditOrgDetailsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Organization Details</DialogTitle>
        <DialogContent>
          {orgDetailsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {orgDetailsError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Display Name"
              value={orgDisplayName}
              onChange={(e) => setOrgDisplayName(e.target.value)}
              fullWidth
              placeholder="e.g., Intelligent Automation"
              helperText="Optional: A friendly display name"
            />
            <TextField
              label="Domain"
              value={orgDomain}
              onChange={(e) => setOrgDomain(e.target.value)}
              fullWidth
              placeholder="e.g., intelligent-automation.net"
              helperText="Optional: Primary domain for this organization"
            />
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={orgSharedRunPool}
                    onChange={(e) => setOrgSharedRunPool(e.target.checked)}
                  />
                }
                label="Shared Run Pool"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                When enabled, all members share the organization's monthly run limit.
                When disabled, each member uses their own individual allocation.
              </Typography>
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={orgMfaRequired}
                    onChange={(e) => setOrgMfaRequired(e.target.checked)}
                  />
                }
                label="Require Two-Factor Authentication (MFA)"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                When enabled, all members must set up an authenticator app (TOTP) before accessing the platform.
                Existing users without MFA will be prompted to set it up on their next login.
              </Typography>
            </Box>
            <TextField
              label="Monthly Run Limit"
              type="number"
              value={orgMaxRuns}
              onChange={(e) => setOrgMaxRuns(e.target.value)}
              fullWidth
              required
              inputProps={{ min: 0, step: 1 }}
              helperText="Maximum number of document processing runs per month"
            />
            {/* Doc Chat / Legal Research limits — IA build only (Trello #380).
                Broker-AI deducts both from the main credit balance, so editing a
                separate cap there changes nothing. See the display tiles above. */}
            {!IS_BROKER_ADMIN && (
              <>
                <TextField
                  label="Document Chat Limit"
                  type="number"
                  value={orgMaxDocChat}
                  onChange={(e) => setOrgMaxDocChat(e.target.value)}
                  fullWidth
                  required
                  inputProps={{ min: 0, step: 1 }}
                  helperText="Maximum number of document chat queries per month"
                />
                <TextField
                  label="Legal Research Limit"
                  type="number"
                  value={orgMaxResearch}
                  onChange={(e) => setOrgMaxResearch(e.target.value)}
                  fullWidth
                  required
                  inputProps={{ min: 0, step: 1 }}
                  helperText="Maximum number of legal research (textbook) queries per month"
                />
              </>
            )}
            {/* Referral source — broker-AI distribution-partner tracking,
                shown only on the broker-AI admin build. (Trello #260) */}
            {IS_BROKER_ADMIN && (
              <ReferralSourceSelect
                labelId="edit-referral-source-label"
                value={orgReferralSource}
                onChange={setOrgReferralSource}
                helperText="Where this organisation was referred from (e.g. FSP Hub) — for referral & revenue-share tracking."
              />
            )}
            {/* EFT payer toggle moved to the consolidated "Billing & Plan" card
                (Trello #332) — it now sits next to the Monthly Amount it drives.
                The org-details save still re-sends the loaded eftPayer value
                (a no-op) so this editor never clobbers it. */}
            {/* Billing status — manually set for the sales-led Intelligent
                Automation tenant (no self-serve subscription to auto-derive from).
                Shown only on the IA admin build; drives the Organizations "Billing"
                column + filter. "Not set" leaves the org blank. */}
            {!IS_BROKER_ADMIN && (
              <TextField
                select
                label="Billing Status"
                value={orgManualBillingStatus}
                onChange={(e) => setOrgManualBillingStatus(e.target.value)}
                fullWidth
                helperText="Manually track whether this org is on trial, unpaid, or paid. Leave as Not set to show blank."
              >
                <MenuItem value="">
                  <em>Not set</em>
                </MenuItem>
                <MenuItem value="trial">Trial</MenuItem>
                <MenuItem value="unpaid">Unpaid</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
              </TextField>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOrgDetailsOpen(false)} disabled={savingOrgDetails}>
            Cancel
          </Button>
          <Button onClick={handleSaveOrgDetails} variant="contained" disabled={savingOrgDetails}>
            {savingOrgDetails ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Generation Confirmation Modal */}
      <Dialog
        open={invoiceModalOpen}
        onClose={() => !invoiceGenerating && setInvoiceModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Generate Invoice - {formatPeriod(invoiceModalPeriod)}
        </DialogTitle>
        <DialogContent>
          {invoiceModalError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setInvoiceModalError(null)}>
              {invoiceModalError}
            </Alert>
          )}
          {/* ONE branch for both stacks since Trello #670.
              The IA build used to render six editable fields here — Customer
              Name, Address, VAT, Description, Licenses, Amount — and POST them.
              The backend has ignored that body since the branded-PDF rewrite
              (`generate-organization-invoice.ts`: "any body is IGNORED"), so the
              admin typed an amount, the amount was discarded, and generation
              then failed BECAUSE the amount was missing. The 400 told him to set
              it on the organisation while the form in front of him was asking
              for it. Everything the invoice prints comes from the org record, so
              this now shows exactly that, for both stacks. */}
          {!billingCompleteForInvoice ? (
            <Alert
              severity="warning"
              data-testid="invoice-missing-fields"
              action={
                <Button
                  color="inherit"
                  size="small"
                  // The dialog is maxWidth="sm", so without this the label wraps
                  // to three lines inside the Alert's action slot.
                  sx={{ whiteSpace: 'nowrap' }}
                  onClick={() => { setInvoiceModalOpen(false); handleEditBillingClick(); }}
                >
                  Edit billing details
                </Button>
              }
            >
              This invoice still needs the organisation&apos;s{' '}
              <strong>{formatMissingFields(missingBillingFields)}</strong>.
              Set {missingBillingFields.length === 1 ? 'it' : 'them'} under{' '}
              <strong>Billing &amp; Plan → Billing details → Edit billing details</strong>,
              then generate.
            </Alert>
          ) : (
            <>
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    sx={{ whiteSpace: 'nowrap' }}
                    onClick={() => { setInvoiceModalOpen(false); handleEditBillingClick(); }}
                  >
                    Edit billing details
                  </Button>
                }
              >
                Generated from the organisation&apos;s billing details — change them there, not here.
              </Alert>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                {([
                  ['Bill To', organization?.billingName || organization?.displayName || organization?.name],
                  ['Address', organization?.billingAddress],
                  ['VAT Number', organization?.vatNumber || '—'],
                  ['Tier', organization?.tierName],
                  // `formatZar` (utils/format), the same helper Bulk Invoices and
                  // the Cost Report use — not a local `toFixed(2)`. This is the
                  // figure the admin checks before committing an invoice number,
                  // and "R 30000.00" is materially harder to read at a glance
                  // than "R 30,000.00", which is also what the PDF prints.
                  ['Monthly Amount', formatZar(Number(organization?.monthlyAmountZAR ?? 0))],
                  // What the description ROW of the PDF will actually read, so
                  // the admin can check it before committing an invoice number.
                  [
                    'Description',
                    organization?.invoiceDescription
                      || `${defaultInvoiceDescriptionLabel} — ${formatPeriod(invoiceModalPeriod)}`,
                  ],
                  ['Billing Month', formatPeriod(invoiceModalPeriod)],
                ] as Array<[string, string | undefined]>).map(([k, v]) => (
                  <Box key={k}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {k}
                    </Typography>
                    <Typography
                      variant="body2"
                      data-testid={k === 'Description' ? 'invoice-preview-description' : undefined}
                      sx={{ fontWeight: 600, whiteSpace: 'pre-line' }}
                    >
                      {v}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoiceModalOpen(false)} disabled={invoiceGenerating}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmGenerateInvoice}
            variant="contained"
            disabled={invoiceGenerating || !billingCompleteForInvoice}
            startIcon={invoiceGenerating ? <CircularProgress size={20} /> : null}
          >
            {invoiceGenerating ? 'Generating...' : 'Generate Invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Organization Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Delete Organization</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDeleteError(null)}>
              {deleteError}
            </Alert>
          )}
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will soft-delete the organization, revoke all its API keys, and remove its domain entries.
            <br /><br />
            <strong>The organization must have zero members.</strong> Remove or reassign all members before deleting.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            To confirm deletion, please type the organization name below:
          </Typography>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 2, fontFamily: 'monospace' }}>
            {organization?.name}
          </Typography>
          <TextField
            fullWidth
            label="Confirm Organization Name"
            value={deleteConfirmName}
            onChange={(e) => setDeleteConfirmName(e.target.value)}
            placeholder={organization?.name}
            disabled={deleting}
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteOrganization}
            disabled={deleting || deleteConfirmName !== organization?.name}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete Organization'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganizationDetailPage;
